import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '../../i18n';
import {apiFetch, apiFetchBlob, apiRequest} from './WorkSheetContext';
import StatisticsHelper from './StatisticsHelper';
import {isAllPlacesScope} from './managerRoles';
import {sortWarehousesByName} from '../utils/sortWarehouses';

/**
 * Local calendar date (`YYYY-MM-DD`) of a Date.
 *
 * Deliberately NOT `toISOString().slice(0, 10)`: that formats the UTC day, so in
 * any timezone ahead of UTC (CET/CEST included) a local-midnight lower bound
 * such as 2026-03-02T00:00+01:00 serialises as 2026-03-01 and every `dateFrom`
 * filter built from a picked range starts one calendar day early.
 * @param {Date} date the date to format.
 * @returns {string} the local `YYYY-MM-DD` date.
 */
const toDateOnly = (date) => {
    const pad = (value) => String(value).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const getDefaultManagementDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        dateFrom: toDateOnly(start),
        dateTo: toDateOnly(end),
    };
};

export const getManagementScope = async () => {
    const [restaurantRaw, placeRaw] = await Promise.all([
        AsyncStorage.getItem('restaurant'),
        AsyncStorage.getItem('restaurantPlace'),
    ]);

    const restaurant = restaurantRaw ? JSON.parse(restaurantRaw) : null;
    const place = placeRaw ? JSON.parse(placeRaw) : null;

    return {
        restaurant,
        place,
        restaurantId: restaurant?._id || restaurant?.id || '',
        placeId: isAllPlacesScope(place) ? '' : (place?._id || place?.id || ''),
        allPlaces: isAllPlacesScope(place),
        placeLabel: isAllPlacesScope(place) ? i18n.t('management_scope_all_places') : (place?.name || place?.raw || i18n.t('management_scope_selected_place')),
    };
};

const normalizeList = (response, keys = []) => {
    if (Array.isArray(response)) return response;
    for (const key of keys) {
        if (Array.isArray(response?.[key])) return response[key];
        if (Array.isArray(response?.data?.[key])) return response.data[key];
        if (Array.isArray(response?.result?.[key])) return response.result[key];
        if (Array.isArray(response?.body?.[key])) return response.body[key];
    }
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.result?.data)) return response.result.data;
    if (Array.isArray(response?.body?.data)) return response.body.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.docs)) return response.docs;
    return [];
};

/**
 * Normalize the `{success, data, pagination}` envelope that the paginated
 * unified-customer and lead sub-resource endpoints answer with into the
 * `{items, pagination}` pair the sales/detail screens consume. The list itself
 * still goes through {@link normalizeList}, so a legacy/array-only body degrades
 * to a single-page result instead of throwing — callers never have to guard the
 * shape themselves.
 * @param {object} body raw response body.
 * @param {string[]} [keys] extra list keys to try before the generic fallbacks.
 * @returns {{items: object[], pagination: {total: number, page: number, limit: number, pages: number}}}
 */
const withPagination = (body, keys = []) => {
    const items = normalizeList(body, keys);
    const meta = body?.pagination || {};

    return {
        items,
        pagination: {
            total: Number(meta.total ?? items.length) || 0,
            page: Number(meta.page ?? 1) || 1,
            limit: Number(meta.limit ?? items.length) || 0,
            pages: Number(meta.pages ?? 1) || 1,
        },
    };
};

/**
 * Pagination-aware variant of {@link normalizeList} for list endpoints that
 * answer with EITHER a nested `pagination` block (unified customers) OR the
 * page metadata at the top level of the body (`/crm/leads`, `/serviceModule/customers`:
 * `{docs|leads, total, page, pages, limit}`). When the body carries no page
 * metadata at all, a full page is taken to mean "there may be more".
 * @param {object} body raw response body.
 * @param {string[]} keys list keys to try (see {@link normalizeList}).
 * @param {{page: number, limit: number}} asked the page window that was requested.
 * @returns {{items: object[], pagination: {total: number, page: number, limit: number, pages: number}}}
 */
const withPageMeta = (body, keys, asked) => {
    const items = normalizeList(body, keys);
    const meta = (body?.pagination && typeof body.pagination === 'object') ? body.pagination : (body && typeof body === 'object' ? body : {});
    const page = Number(meta.page) || asked.page;
    const pagesRaw = Number(meta.pages);
    const pages = Number.isFinite(pagesRaw) && pagesRaw > 0
        ? pagesRaw
        : (items.length >= asked.limit ? page + 1 : page);

    return {
        items,
        pagination: {
            total: Number(meta.total) || items.length,
            page,
            limit: Number(meta.limit) || asked.limit,
            pages,
        },
    };
};

const buildQuery = (params = {}) => Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

const scopedParams = (scope, extra = {}) => {
    return buildQuery({
        ...extra,
        ...(scope?.placeId ? {placeId: scope.placeId} : {}),
    });
};

/**
 * Build the scope headers that accompany a management API call. Several backend
 * endpoints (the whole `serviceModule/*` family, `finance/registers`, `banks`
 * and `restaurant/nav-invoices`) resolve the active store from an
 * `x-restaurant-id` header and otherwise fall back to the caller's PRIMARY
 * restaurant. Without this header a multi-store manager who selected store B
 * still sees store A's technicians / appointments / registers — or an empty list
 * when the primary restaurant has no service data at all. Sending the selected
 * restaurant id makes every scoped list resolve to the store the user is actually
 * looking at. Returns an empty object when no restaurant is in scope so single
 * users and the (header-less) admin path are completely unaffected.
 * @param {{restaurantId?: string}} [scope] the active management scope.
 * @returns {Record<string, string>} headers to merge into an `apiFetch` options object.
 */
const scopeHeaders = (scope) => (scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {});

/**
 * `apiFetch` variant safe for endpoints that answer `204`/`205` (No Content)
 * on success — e.g. `DELETE /banks/{id}`. Calling `.json()` on an empty body
 * throws, so this reads the raw `Response` via {@link apiRequest} and only
 * parses JSON when the status code implies one is present.
 * @param {string} path request path (appended to `API_URL`).
 * @param {object} [options] fetch options (method, headers, body).
 * @returns {Promise<object|null>} the parsed body, or `null` for No Content.
 */
const apiFetchAllowEmpty = async (path, options = {}) => {
    const res = await apiRequest(path, options);
    if (res.status === 204 || res.status === 205) return null;
    return res.json();
};

/**
 * Extract the HTTP status code embedded in an {@link apiFetch}/apiRequest
 * failure message (`API ${status}: ${body}`, see WorkSheetContext#apiRequest).
 * @param {Error} err the thrown error.
 * @returns {?number} the parsed status code, or `null` when it can't be read.
 */
const parseApiErrorStatus = (err) => {
    const match = /^API (\d{3}):/.exec(String(err?.message || ''));
    return match ? Number(match[1]) : null;
};

export const fetchManagementOrders = async (scope, range = getDefaultManagementDateRange()) => {
    if (!scope?.restaurantId) return [];
    const query = scopedParams(scope, {
        startDate: range.startDate,
        endDate: range.endDate,
        all: 'true',
        mobile: 'true',
    });
    return normalizeList(await apiFetch(`/restaurant/${scope.restaurantId}/order?${query}`), ['orders']);
};

/**
 * Fast aggregated order KPI summary for the management dashboard — server-side
 * aggregation over the ArchiveOrder mirror (GET /order-statistics/summary),
 * replacing the "pull every order and aggregate on-device" path. Returns the
 * small, flat summary payload (totals + revenue + keyed breakdowns) or null when
 * out of scope / on error, so callers can fall back to the client-side builders.
 * @param {{restaurantId?: string, placeId?: string}} scope active management scope.
 * @param {{startDate?: string, endDate?: string}} [range] date window (defaults to last 30 days).
 * @returns {Promise<object|null>} summary payload, or null.
 */
export const fetchOrderStatisticsSummary = async (scope, range = getDefaultManagementDateRange()) => {
    if (!scope?.restaurantId) return null;
    const query = scopedParams(scope, {
        restaurantId: scope.restaurantId,
        startDate: range.startDate,
        endDate: range.endDate,
    });
    try {
        const res = await apiFetch(`/order-statistics/summary?${query}`, {headers: scopeHeaders(scope)});
        return res && typeof res === 'object' ? (res.data || res.body || res) : null;
    } catch (_error) {
        return null;
    }
};

/**
 * Fast bucketed revenue/orders trend for the management dashboard chart — server
 * aggregation over the ArchiveOrder mirror (GET /order-statistics/timeseries).
 * @param {{restaurantId?: string, placeId?: string}} scope active management scope.
 * @param {{range?: {startDate?: string, endDate?: string}, interval?: string}} [options]
 *   window + bucket size (day/week/month/quarter/year; default month).
 * @returns {Promise<Array<{bucket: string, count: number, gross: number, net: number}>>}
 */
export const fetchOrderStatisticsTimeseries = async (scope, {range = getDefaultManagementDateRange(), interval = 'month'} = {}) => {
    if (!scope?.restaurantId) return [];
    const query = scopedParams(scope, {
        restaurantId: scope.restaurantId,
        startDate: range.startDate,
        endDate: range.endDate,
        interval,
    });
    try {
        const res = await apiFetch(`/order-statistics/timeseries?${query}`, {headers: scopeHeaders(scope)});
        const payload = res?.data || res?.body || res;
        return Array.isArray(payload?.buckets) ? payload.buckets : [];
    } catch (_error) {
        return [];
    }
};

/**
 * List a store's products for the Management "Termékek" screen
 * (GET /restaurant/{restaurantId}/foods/mobile). A bare call (no `options`)
 * keeps the pre-search behaviour every existing caller (e.g. the dashboard
 * aggregator, {@link fetchManagementDashboardData}) already expects: page 1,
 * up to 100 rows, disabled products included, returned as a flat array.
 *
 * Passing `search`, `categoryId`, `page` or `limit` switches the backend to
 * its paginated envelope (`{page, pages, from, to, count, data}` — verified
 * against `getRestaurantFoodsMobile` in `restarurant.swagger.yaml`), which
 * this helper normalizes to `{items, pagination}` so the list screen can
 * render server-side search/filter plus a "load more" footer.
 * @param {{restaurantId?: string, placeId?: string}} scope active management scope.
 * @param {object} [options] server-side search/filter/paging — omit for the
 *   legacy flat-array call.
 * @param {string} [options.search] free-text query (name, category, tags, barcode, SKU).
 * @param {string} [options.categoryId] restrict to one FoodCategory id.
 * @param {number} [options.page] 1-based page number (default 1).
 * @param {number} [options.limit] page size (default 30).
 * @returns {Promise<Array<object>|{items: Array<object>, pagination: {page: number, pages: number, total: number, limit: number}}>}
 */
export const fetchManagementFoods = async (scope, options = {}) => {
    if (!scope?.restaurantId) return [];
    const paginated = !!(options.search || options.categoryId || options.page || options.limit);
    if (!paginated) {
        const query = scopedParams(scope, {page: '1', limit: '100', showDisabled: 'true'});
        return normalizeList(await apiFetch(`/restaurant/${scope.restaurantId}/foods/mobile?${query}`), ['foods']);
    }
    const query = scopedParams(scope, {
        categoryId: options.categoryId || undefined,
        search: options.search || undefined,
        page: String(options.page || 1),
        limit: String(options.limit || 30),
        showDisabled: 'true',
    });
    const body = await apiFetch(`/restaurant/${scope.restaurantId}/foods/mobile?${query}`);
    const items = normalizeList(body, ['foods']);
    return {
        items,
        pagination: {
            page: Number(body?.page ?? options.page ?? 1) || 1,
            pages: Number(body?.pages ?? 1) || 1,
            total: Number(body?.count ?? items.length) || 0,
            limit: Number(options.limit || items.length) || 0,
        },
    };
};

/**
 * Load a store's enabled/visible product categories for the Management
 * products filter chips and the product create sheet's category select
 * (GET /restaurant/{restaurantId}/categories/mobile — pre-filtered,
 * pre-ordered, already excludes soft-deleted "Törölt" placeholders).
 * Normalized to `[{_id, name, isExtra}]`; extra/topping categories are
 * dropped — a product's PRIMARY category is never an extras bucket, mirrors
 * the `!cat.isExtra` filter ManagementDetailScreen already applies to its
 * own category select (see QA MGMT-0xx).
 * @param {{restaurantId?: string}} [scope] active scope (resolved from storage when omitted).
 * @returns {Promise<Array<{_id: string, name: string}>>}
 */
export const fetchManagementCategories = async (scope) => {
    const resolved = scope || await getManagementScope();
    if (!resolved?.restaurantId) return [];
    const list = normalizeList(await apiFetch(`/restaurant/${resolved.restaurantId}/categories/mobile`), ['categories']);
    return list
        .map((cat) => ({
            _id: String(cat?._id || cat?.id || ''),
            name: String(cat?.name?.hu || cat?.name?.en || cat?.name || ''),
            isExtra: Boolean(cat?.isExtra),
        }))
        .filter((cat) => cat._id && !cat.isExtra);
};

/**
 * Fetch the store's most-sold products, ranked by quantity sold over a window.
 * Returns the lean `[{ foodId, soldQty, revenue }]` rows the backend aggregates
 * from closed, non-storno orders; clients join by `foodId` to re-order their
 * cached catalog by popularity. Place scope flows through `scopedParams`
 * (placeId) + `scopeHeaders` (x-restaurant-id), exactly like the other reads.
 * @param {{restaurantId?: string, placeId?: string}} scope active management scope.
 * @param {{period?: string, limit?: number}} [options] window preset + row cap.
 * @returns {Promise<Array<{foodId: string, soldQty: number, revenue: number}>>}
 */
export const fetchMostSoldFoods = async (scope, {period = 'last_30_days', limit = 300} = {}) => {
    if (!scope?.restaurantId) return [];
    const query = scopedParams(scope, {period, limit: String(limit)});
    return normalizeList(
        await apiFetch(`/restaurant/${scope.restaurantId}/foods/most-sold?${query}`, {headers: scopeHeaders(scope)}),
        ['data', 'foods'],
    );
};

export const fetchManagementStock = async (scope) => {
    if (!scope?.restaurantId) return [];
    const query = scopedParams(scope, {restaurantId: scope.restaurantId, page: '1', limit: '100', sortField: 'stock', sortDir: 'asc'});
    return normalizeList(await apiFetch(`/warehouse/all/stock?${query}`), ['stock']);
};

export const fetchManagementInvoices = async (scope, directionOrOptions = 'INBOUND', range = getDefaultManagementDateRange()) => {
    if (!scope?.restaurantId) return [];
    const options = typeof directionOrOptions === 'object' && directionOrOptions !== null
        ? directionOrOptions
        : {direction: directionOrOptions, range};
    const normalizedRange = options.range || getDefaultManagementDateRange();
    const direction = options.direction || 'INBOUND';
    const status = options.status || undefined;
    // Background callers (the dashboard poll) pass `unboundedFallback: false`: when the
    // selected window is simply empty they must NOT fall through to the two date-less
    // requests below, which pull the store's ENTIRE invoice history on every tick.
    const unboundedFallback = options.unboundedFallback !== false;
    const placeId = scope?.placeId || undefined;
    const dateFrom = normalizedRange.dateFrom;
    const dateTo = normalizedRange.dateTo;

    // Primary: same endpoint used by the admin web app. It resolves the store from
    // an `x-restaurant-id` header (validated against the caller's own restaurants),
    // so a multi-store manager sees the SELECTED store's invoices, not always the
    // first one.
    const headers = scopeHeaders(scope);
    const primaryBounded = buildQuery({direction, dateFrom, dateTo, status, placeId});
    const primary = normalizeList(await apiFetch(`/restaurant/nav-invoices?${primaryBounded}`, {headers}), ['invoices', 'navInvoices']);
    if (primary.length > 0) return primary;

    // Legacy fallback: direct NAV endpoint with explicit restaurantId.
    const legacyBounded = buildQuery({
        restaurantId: scope.restaurantId,
        direction,
        startDate: dateFrom,
        endDate: dateTo,
        status,
        placeId,
    });
    const legacy = normalizeList(await apiFetch(`/navInvoices?${legacyBounded}`), ['invoices', 'navInvoices']);
    if (legacy.length > 0) return legacy;

    if (!unboundedFallback) return legacy;

    // Final fallback: retry both endpoints without date bounds for older datasets.
    const primaryUnbounded = buildQuery({direction, status, placeId});
    const unboundedPrimary = normalizeList(await apiFetch(`/restaurant/nav-invoices?${primaryUnbounded}`, {headers}), ['invoices', 'navInvoices']);
    if (unboundedPrimary.length > 0 || status) return unboundedPrimary;

    const legacyUnbounded = buildQuery({restaurantId: scope.restaurantId, direction, status, placeId});
    return normalizeList(await apiFetch(`/navInvoices?${legacyUnbounded}`), ['invoices', 'navInvoices']);
};

/**
 * First page (50) of the CRM leads (GET /crm/leads). The optional filters are
 * all SERVER-side so a narrowed list is never limited to the loaded page.
 * @param {object} scope active management scope.
 * @param {{category?: string, sortBy?: string, aiResearch?: string, assignedTo?: string}} [options] `category` = comma list of category
 *   keys (`none` = uncategorized), `sortBy` = `createdAt` (default) | `category`, `aiResearch` = `done|none|running|failed`,
 *   `assignedTo` = a user id or the literal `null` (nobody owns the lead).
 * @returns {Promise<object[]>} the lead rows.
 */
export const fetchManagementLeads = async (scope, options = {}) => {
    const sortBy = options.sortBy === 'category' ? 'category' : 'createdAt';
    const sortOrder = sortBy === 'category' ? 'asc' : 'desc';
    const page = Number(options.page) || 1;
    const limit = Number(options.limit) || 50;
    const extra = [];

    if (options.category) extra.push(`category=${encodeURIComponent(options.category)}`);
    if (options.aiResearch) extra.push(`aiResearch=${encodeURIComponent(options.aiResearch)}`);
    if (options.assignedTo) extra.push(`assignedTo=${encodeURIComponent(options.assignedTo)}`);
    if (options.search) extra.push(`search=${encodeURIComponent(options.search)}`);

    const body = await apiFetch(`/crm/leads?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}${extra.length ? `&${extra.join('&')}` : ''}`, {headers: scopeHeaders(scope)});

    return options.paged ? withPageMeta(body, ['leads'], {page, limit}) : normalizeList(body, ['leads']);
};

export const fetchManagementCustomers = async (scope, options = {}) => {
    const page = Number(options.page) || 1;
    const limit = Number(options.limit) || 50;
    const body = await apiFetch(`/serviceModule/customers?${buildQuery({page, limit, search: options.search})}`, {headers: scopeHeaders(scope)});

    return options.paged ? withPageMeta(body, ['customers'], {page, limit}) : normalizeList(body, ['customers']);
};

/* -------------------------------------------------------------------------- */
/*  Unified customers (store + partner + order guest + CRM, one merged index)   */
/* -------------------------------------------------------------------------- */

/**
 * Search the UNIFIED customer index (GET /customers/unified/search) — one merged,
 * deduplicated view over WooCommerce customers, partners, order-derived guests
 * and Service Module (CRM) customers. Unlike {@link fetchManagementCustomers}
 * (which only ever sees the `ServiceModuleCustomer` collection) this is the list
 * a salesperson needs: every person the store has ever sold to.
 *
 * `includeRelatedStats` defaults to true server-side, so each row carries a
 * `relatedStats: {orders, invoices, quotes, workOrders}` block the list screen
 * renders as its order-count meta. Passing `hasOrders`/`hasQuotes` implicitly
 * enables that computation on the backend as well.
 *
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} [options] optional filters.
 * @param {string} [options.search] free-text query (email, name, phone, company).
 * @param {string} [options.types] comma-separated sources (`restaurant,serviceModule`).
 * @param {boolean} [options.hasOrders] keep only customers with (true) / without (false) an order.
 * @param {boolean} [options.hasQuotes] keep only customers with (true) / without (false) a quote.
 * @param {boolean} [options.includeRelatedStats] attach per-customer related counts (default true).
 * @param {number} [options.page] 1-based page number (default 1).
 * @param {number} [options.limit] page size, capped at 100 by the backend (default 50).
 * @param {boolean} [options.paged] answer `{items, pagination}` instead of the bare array.
 * @returns {Promise<object[]|{items: object[], pagination: object}>} the unified customers on the requested page.
 */
export const fetchManagementUnifiedCustomers = async (scope, options = {}) => {
    const query = buildQuery({
        restaurantId: scope?.restaurantId,
        search: options.search,
        types: options.types,
        hasOrders: options.hasOrders,
        hasQuotes: options.hasQuotes,
        includeRelatedStats: options.includeRelatedStats,
        page: options.page || 1,
        limit: options.limit || 50,
    });

    const body = await apiFetch(`/customers/unified/search?${query}`, {headers: scopeHeaders(scope)});

    return options.paged
        ? withPageMeta(body, ['customers'], {page: Number(options.page) || 1, limit: Number(options.limit) || 50})
        : normalizeList(body, ['customers']);
};

/**
 * Load ONE unified customer (GET /customers/unified/{customerId}). The id is
 * resolved across every source collection, so the caller does not need to know
 * which bucket the record lives in. With `includeRelated` (default true) the
 * response also carries the newest related orders/invoices/quotes/work orders
 * plus their total counts.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} customerId the unified customer id.
 * @param {object} [options] optional lookup tuning.
 * @param {boolean} [options.includeRelated] attach the related-entity lists (default true).
 * @param {number} [options.relatedLimit] items per related list, capped at 30 (default 8).
 * @param {string} [options.type] force a source (`restaurant` | `serviceModule`).
 * @returns {Promise<{customer: ?object, source: string, relatedStats: object, related: object}>}
 */
export const fetchUnifiedCustomer = async (scope, customerId, options = {}) => {
    const query = buildQuery({
        restaurantId: scope?.restaurantId,
        type: options.type,
        includeRelated: options.includeRelated,
        relatedLimit: options.relatedLimit,
    });
    const body = await apiFetch(
        `/customers/unified/${encodeURIComponent(customerId)}${query ? `?${query}` : ''}`,
        {headers: scopeHeaders(scope)},
    );

    return {
        customer: body?.data || null,
        source: body?.type || '',
        relatedStats: body?.relatedStats || {},
        related: body?.related || {},
    };
};

/**
 * Page through a unified customer's FULL order history
 * (GET /customers/unified/{customerId}/orders). The detail endpoint only exposes
 * the newest `relatedLimit` orders, so this is the endpoint a customer detail
 * screen pages with.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} customerId the unified customer id.
 * @param {object} [options] optional filters.
 * @param {string} [options.status] comma-separated order statuses to keep.
 * @param {string} [options.orderType] comma-separated order types to keep.
 * @param {string} [options.dateFrom] inclusive ISO lower bound on `createdAt`.
 * @param {string} [options.dateTo] inclusive ISO upper bound on `createdAt`.
 * @param {string} [options.sort] sort expression (default `-createdAt`).
 * @param {number} [options.page] 1-based page number (default 1).
 * @param {number} [options.limit] page size, capped at 100 (default 20).
 * @returns {Promise<{items: object[], pagination: object}>}
 */
export const fetchUnifiedCustomerOrders = async (scope, customerId, options = {}) => {
    const query = buildQuery({
        restaurantId: scope?.restaurantId,
        type: options.type,
        status: options.status,
        orderType: options.orderType,
        dateFrom: options.dateFrom,
        dateTo: options.dateTo,
        sort: options.sort,
        page: options.page || 1,
        limit: options.limit || 20,
    });

    return withPagination(
        await apiFetch(`/customers/unified/${encodeURIComponent(customerId)}/orders?${query}`, {headers: scopeHeaders(scope)}),
        ['orders'],
    );
};

/**
 * Page through a unified customer's activity feed
 * (GET /customers/unified/{customerId}/timeline) — the record's `history[]`
 * merged with its notes (`comments[]`), newest first. Each entry carries a
 * `kind` of `history` | `comment`.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} customerId the unified customer id.
 * @param {object} [options] optional paging.
 * @param {number} [options.page] 1-based page number (default 1).
 * @param {number} [options.limit] page size, capped at 100 (default 20).
 * @param {string} [options.type] force a source (`restaurant` | `serviceModule`).
 * @returns {Promise<{items: object[], pagination: object}>}
 */
export const fetchUnifiedCustomerTimeline = async (scope, customerId, options = {}) => {
    const query = buildQuery({
        restaurantId: scope?.restaurantId,
        type: options.type,
        page: options.page || 1,
        limit: options.limit || 20,
    });

    return withPagination(
        await apiFetch(`/customers/unified/${encodeURIComponent(customerId)}/timeline?${query}`, {headers: scopeHeaders(scope)}),
        ['events', 'timeline'],
    );
};

/**
 * Append a note to a unified customer (POST /customers/unified/{customerId}/notes).
 * The body key is `text` (NOT `note` — that is the LEAD note contract, see
 * {@link addLeadNote}); the note is capped at 2000 characters server-side and
 * shows up immediately in {@link fetchUnifiedCustomerTimeline}. Order-derived
 * guests are read-only and answer 409.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} customerId the unified customer id.
 * @param {string} text the note body.
 * @returns {Promise<object>} the updated customer payload.
 */
export const addUnifiedCustomerNote = async (scope, customerId, text) => apiFetch(
    `/customers/unified/${encodeURIComponent(customerId)}/notes`,
    {method: 'POST', headers: scopeHeaders(scope), body: JSON.stringify({text})},
);

/**
 * Resolve an address to coordinates for the delivery-pin picker
 * (POST /customers/unified/geocode). Initial pin placement only — the user can
 * always drag the marker, so a miss is not an error: 404 (address not found) and
 * 503 (no geocoder configured) both resolve `null`, and so does a network failure.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {{address?: string, zip?: string, city?: string, country?: string}} query one-line `address`, or the structured parts.
 * @returns {Promise<?{lat: number, lng: number, formattedAddress: string, precision: string}>} the match, `null` when none.
 */
export const geocodeCustomerAddress = async (scope, query) => {
    try {
        const body = await apiFetch('/customers/unified/geocode', {
            method: 'POST',
            headers: {'Content-Type': 'application/json', ...scopeHeaders(scope)},
            body: JSON.stringify(query || {}),
        });
        const hit = body?.data && typeof body.data === 'object' ? body.data : body;

        return Number.isFinite(Number(hit?.lat)) && Number.isFinite(Number(hit?.lng)) ? hit : null;
    } catch (_error) {
        return null;
    }
};

/**
 * Archive (soft-delete) a unified customer (DELETE /customers/unified/{customerId}).
 * `record.type` (`'restaurant'` | `'serviceModule'`, as normalized by the
 * backend on every unified-customer read) MUST be echoed back in the query
 * string — the endpoint resolves the underlying collection from it, and a
 * missing/wrong value silently 404s instead of archiving anything. Order-derived
 * guests are read-only and answer 409 — surface that as "convert first", never
 * as a generic archive failure.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} record the unified customer record (`_id`/`id` + `type`).
 * @returns {Promise<object>} the backend's archive confirmation payload.
 */
export const archiveUnifiedCustomer = async (scope, record) => {
    const id = idOf(record);
    const query = buildQuery({restaurantId: scope?.restaurantId, type: record?.type});

    return apiFetch(`/customers/unified/${encodeURIComponent(id)}${query ? `?${query}` : ''}`, {
        method: 'DELETE',
        headers: scopeHeaders(scope),
    });
};

/* -------------------------------------------------------------------------- */
/*  Generic entity contacts (GET|POST /contacts/{entityType}/{entityId}, …)    */
/* -------------------------------------------------------------------------- */

/**
 * Map a unified-customer row's `source` (`restaurant` | `partner` | `guest` |
 * `serviceModule`) onto the `entityType` the generic contacts API
 * (`CONTACT_ENTITY_REGISTRY` in `contact.service.js`) expects. The coarse
 * `type` field the unified search/detail response carries (`restaurant` |
 * `serviceModule`) collapses partner/guest into `restaurant`, which is NOT a
 * valid contacts entity type — this reads the finer-grained `source` instead.
 * @param {{source?: string, type?: string}} record a unified customer row.
 * @returns {string} one of `customer` | `partner` | `guest` | `service_customer`.
 */
export const resolveCustomerContactEntityType = (record) => {
    const source = String(record?.source || record?.type || '').toLowerCase();

    if (source === 'servicemodule' || source === 'service_customer') return 'service_customer';
    if (source === 'partner') return 'partner';
    if (source === 'guest') return 'guest';

    return 'customer';
};

/**
 * List every contact attached to an entity (GET /contacts/{entityType}/{entityId}).
 * A not-yet-migrated entity still answers with one synthesized primary contact
 * built from its legacy name/email/phone fields, so the UI never renders empty
 * for a record that predates multi-contact support.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType one of `lead` | `customer` | `guest` | `service_customer` | `partner` | `provider` | `project` | `quote` | `asset`.
 * @param {string} entityId the owning entity's id.
 * @returns {Promise<object[]>} the entity's contacts.
 */
export const fetchEntityContacts = async (scope, entityType, entityId) => {
    const query = buildQuery({restaurantId: scope?.restaurantId});

    return normalizeList(
        await apiFetch(`/contacts/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${query ? `?${query}` : ''}`, {headers: scopeHeaders(scope)}),
        ['contacts'],
    );
};

/**
 * Add a contact to an entity (POST /contacts/{entityType}/{entityId}). The
 * first contact ever added — or one sent with `isPrimary: true` — becomes the
 * primary and is mirrored back into the entity's legacy name/email/phone fields.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType see {@link fetchEntityContacts}.
 * @param {string} entityId the owning entity's id.
 * @param {object} payload `{firstName?, lastName?, name?, email?, phone?, mobile?, jobTitle?, role?, isPrimary?}`.
 * @returns {Promise<object>} the persisted contact subdocument.
 */
export const addEntityContact = async (scope, entityType, entityId, payload) => apiFetch(
    `/contacts/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${scope?.restaurantId ? `?restaurantId=${encodeURIComponent(scope.restaurantId)}` : ''}`,
    {method: 'POST', headers: scopeHeaders(scope), body: JSON.stringify(payload)},
);

/**
 * Update one contact's fields (PUT /contacts/{entityType}/{entityId}/{contactId}).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType see {@link fetchEntityContacts}.
 * @param {string} entityId the owning entity's id.
 * @param {string} contactId the contact subdocument's id.
 * @param {object} payload the changed contact fields.
 * @returns {Promise<object>} the updated contact subdocument.
 */
export const updateEntityContact = async (scope, entityType, entityId, contactId, payload) => apiFetch(
    `/contacts/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/${encodeURIComponent(contactId)}${scope?.restaurantId ? `?restaurantId=${encodeURIComponent(scope.restaurantId)}` : ''}`,
    {method: 'PUT', headers: scopeHeaders(scope), body: JSON.stringify(payload)},
);

/**
 * Remove one contact from an entity (DELETE /contacts/{entityType}/{entityId}/{contactId}).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType see {@link fetchEntityContacts}.
 * @param {string} entityId the owning entity's id.
 * @param {string} contactId the contact subdocument's id.
 * @returns {Promise<object>} the backend's deletion confirmation payload.
 */
export const deleteEntityContact = async (scope, entityType, entityId, contactId) => apiFetch(
    `/contacts/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/${encodeURIComponent(contactId)}${scope?.restaurantId ? `?restaurantId=${encodeURIComponent(scope.restaurantId)}` : ''}`,
    {method: 'DELETE', headers: scopeHeaders(scope)},
);

/**
 * Promote one contact to primary (PATCH /contacts/{entityType}/{entityId}/{contactId}/primary).
 * Demotes every other contact on the same entity and re-mirrors the legacy
 * name/email/phone fields to the newly primary contact.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType see {@link fetchEntityContacts}.
 * @param {string} entityId the owning entity's id.
 * @param {string} contactId the contact subdocument's id to promote.
 * @returns {Promise<object>} the updated contact list.
 */
export const setPrimaryEntityContact = async (scope, entityType, entityId, contactId) => apiFetch(
    `/contacts/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/${encodeURIComponent(contactId)}/primary${scope?.restaurantId ? `?restaurantId=${encodeURIComponent(scope.restaurantId)}` : ''}`,
    {method: 'PATCH', headers: scopeHeaders(scope)},
);

/**
 * Page through the orders linked to a CRM lead (GET /crm/leads/{id}/orders) —
 * the orders whose `lead` reference points at it, newest first. A lead outside
 * the caller's CRM visibility answers 404 (never 403), so surface it as "not
 * available" rather than "deleted".
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} leadId the lead id.
 * @param {object} [options] optional filters.
 * @param {string} [options.status] comma-separated order statuses (unknown value → 400).
 * @param {string} [options.dateFrom] inclusive ISO lower bound on the creation date.
 * @param {string} [options.dateTo] inclusive ISO upper bound on the creation date.
 * @param {string} [options.sort] sort expression (default `-createdAt`).
 * @param {number} [options.page] 1-based page number (default 1).
 * @param {number} [options.limit] page size, capped at 100 (default 20).
 * @returns {Promise<{items: object[], pagination: object}>}
 */
export const fetchLeadOrders = async (scope, leadId, options = {}) => {
    const query = buildQuery({
        status: options.status,
        dateFrom: options.dateFrom,
        dateTo: options.dateTo,
        sort: options.sort,
        page: options.page || 1,
        limit: options.limit || 20,
    });

    return withPagination(
        await apiFetch(`/crm/leads/${encodeURIComponent(leadId)}/orders?${query}`, {headers: scopeHeaders(scope)}),
        ['orders'],
    );
};

/**
 * Page through a lead's activity timeline (GET /crm/leads/{id}/timeline). The
 * lead list only ever embeds the latest 5 events. Notes written through
 * {@link addLeadNote} are read back here with `{event: 'Note'}`.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} leadId the lead id.
 * @param {object} [options] optional filters.
 * @param {string} [options.event] comma-separated event labels to keep (e.g. `Note`).
 * @param {number} [options.page] 1-based page number (default 1).
 * @param {number} [options.limit] page size, capped at 100 (default 20).
 * @returns {Promise<{items: object[], pagination: object}>}
 */
export const fetchLeadTimeline = async (scope, leadId, options = {}) => {
    const query = buildQuery({
        event: options.event,
        page: options.page || 1,
        limit: options.limit || 20,
    });

    return withPagination(
        await apiFetch(`/crm/leads/${encodeURIComponent(leadId)}/timeline?${query}`, {headers: scopeHeaders(scope)}),
        ['events', 'timeline'],
    );
};

/**
 * Append a note to a lead (POST /crm/leads/{id}/notes). The body key is `note`
 * (the unified-customer contract uses `text` — see {@link addUnifiedCustomerNote}).
 * Stored as a `Note` timeline event, so it NEVER advances the pipeline status;
 * an empty/over-long (>5000 chars) note answers 422.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} leadId the lead id.
 * @param {string} note the note body.
 * @returns {Promise<object>} the created timeline event.
 */
export const addLeadNote = async (scope, leadId, note) => apiFetch(
    `/crm/leads/${encodeURIComponent(leadId)}/notes`,
    {method: 'POST', headers: scopeHeaders(scope), body: JSON.stringify({note})},
);

// Lead contacts are NOT a separate API: they are the generic entity-contact
// endpoints with entityType 'lead'. The five lead-specific wrappers that used
// to live here were deleted along with the duplicate LeadContactsPanel — use
// fetchEntityContacts/addEntityContact/updateEntityContact/deleteEntityContact/
// setPrimaryEntityContact with 'lead' instead, so one contacts implementation
// serves every entity the backend's CONTACT_ENTITY_REGISTRY supports.

/**
 * List the quotes attached to a lead (GET /quotes?leadId=&sortBy=createdAt&sortOrder=desc),
 * newest first. No `page`/`limit` is sent, so the backend answers the full
 * unpaginated array rather than the `{docs, totalDocs, ...}` envelope.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} leadId the lead id.
 * @returns {Promise<object[]>} the lead's quotes.
 */
export const fetchLeadQuotes = async (scope, leadId) => normalizeList(
    await apiFetch(
        `/quotes?leadId=${encodeURIComponent(leadId)}&sortBy=createdAt&sortOrder=desc`,
        {headers: scopeHeaders(scope)},
    ),
    ['quotes', 'docs'],
);

/**
 * List the users a lead can be assigned/owned by (GET /crm/assignable-users) —
 * every user in the caller's store scope holding an admin/manager/sales role.
 * Feeds the owner/"assigned to" picker on the lead detail screen.
 * @param {{restaurantId?: string}} scope active management scope.
 * @returns {Promise<object[]>} `[{_id, name:{first,last}, email}]`.
 */
export const fetchAssignableUsers = async (scope) => normalizeList(
    await apiFetch('/crm/assignable-users', {headers: scopeHeaders(scope)}),
    ['users'],
);

/**
 * Run the "Ask from AI" lead research (POST /crm/leads/{id}/ai-research).
 * Gathers Apollo + HU company-registry + public-web intelligence and
 * synthesizes a salesperson-ready briefing; a cached lens in the same language
 * is served for free unless `refresh`/`regenerate` is set. Only the AI
 * synthesis step spends AI credits — a cache hit never does.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} leadId the lead id.
 * @param {{summaryType?: ('extended'|'short'|'warnings'|'positives'|'call_prep'),
 *   refresh?: boolean, regenerate?: boolean, locale?: string, customPrompt?: string}} [options]
 * @returns {Promise<{summary: object, sources: object[], gatheredAt: ?string,
 *   cached: boolean, credits: {charged: number}}>}
 */
export const runLeadAiResearch = async (scope, leadId, options = {}) => apiFetch(
    `/crm/leads/${encodeURIComponent(leadId)}/ai-research`,
    {method: 'POST', headers: scopeHeaders(scope), body: JSON.stringify(options)},
);

/**
 * Work orders (GET /serviceModule/work-orders). Without options: the legacy newest-50 bare list the dashboard reads.
 * With `search` / `page` the SERVER filters and pages, so a work order is found by anything on it (number, licence
 * plate, VIN, vehicle make / model, customer, part name / SKU / barcode, notes ...) and not just within the loaded page.
 * @param {object} scope active management scope.
 * @param {{search?: string, page?: number, limit?: number, paged?: boolean}} [options] `paged` answers `{items, pagination}`.
 * @returns {Promise<object[]|{items: object[], pagination: object}>} the work-order rows.
 */
export const fetchManagementWorkOrders = async (scope, options = {}) => {
    if (!options.paged && !options.search && !options.page) {
        return normalizeList(await apiFetch('/serviceModule/work-orders?limit=50&sort=-workOrderNumber', {headers: scopeHeaders(scope)}), ['workOrders', 'worksheets']);
    }

    const page = Number(options.page) || 1;
    const limit = Number(options.limit) || 50;
    const body = await apiFetch(`/serviceModule/work-orders?${buildQuery({page, limit, search: options.search, envelope: true})}`, {headers: scopeHeaders(scope)});

    return options.paged ? withPageMeta(body, ['workOrders', 'worksheets'], {page, limit}) : normalizeList(body, ['workOrders', 'worksheets']);
};

export const fetchManagementCostTypes = async (scope) => {
    if (!scope?.restaurantId) return [];
    return normalizeList(await apiFetch(`/costTypes?restaurantId=${encodeURIComponent(scope.restaurantId)}`), ['costTypes']);
};

/**
 * List operating expenses for a store (GET /expenses?restaurantId=&dateFrom=&dateTo=&category=&status=).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {{dateFrom?: string, dateTo?: string, category?: string, status?: string}} [options] optional filters.
 * @returns {Promise<object[]>} the store's expense records.
 */
export const fetchManagementExpenses = async (scope, options = {}) => {
    if (!scope?.restaurantId) return [];
    const query = buildQuery({
        restaurantId: scope.restaurantId,
        dateFrom: options.dateFrom,
        dateTo: options.dateTo,
        category: options.category,
        status: options.status,
    });
    return normalizeList(await apiFetch(`/expenses?${query}`, {headers: scopeHeaders(scope)}), ['expenses']);
};

/* -------------------------------------------------------------------------- */
/* Quote templates (full-HTML gallery, restaurant-scoped selection shared) */
/* -------------------------------------------------------------------------- */

/**
 * List quote templates visible to the active store, grouped into system + custom.
 * @param {{restaurantId?: string}} scope
 * @param {{category?: string}} [opts]
 * @returns {Promise<{system: object[], restaurant: object[]}>}
 */
export const fetchQuoteTemplates = async (scope, opts = {}) => {
    const query = buildQuery({category: opts.category});
    const body = await apiFetch(
        `/quote-templates${query ? `?${query}` : ''}`,
        {headers: scopeHeaders(scope)},
    );

    return {
        system: Array.isArray(body?.system) ? body.system : [],
        restaurant: Array.isArray(body?.restaurant) ? body.restaurant : [],
    };
};

/**
 * The restaurant's selected quote template (or the system default). Shared across
 * all users of the store via the `x-restaurant-id` scope header.
 * @param {{restaurantId?: string}} scope
 * @returns {Promise<object|null>}
 */
export const fetchSelectedQuoteTemplate = async (scope) => {
    const body = await apiFetch('/quote-templates/selected', {headers: scopeHeaders(scope)});

    return body?.template || null;
};

/**
 * Persist the restaurant-scoped selected-template pointer.
 * @param {{restaurantId?: string}} scope
 * @param {string} templateId
 * @returns {Promise<object|null>}
 */
export const setSelectedQuoteTemplate = async (scope, templateId) => {
    const body = await apiFetch('/quote-templates/selected', {
        method: 'PUT',
        headers: scopeHeaders(scope),
        body: JSON.stringify({templateId}),
    });

    return body?.template || null;
};

/**
 * Render a quote to PDF (backend Puppeteer) using the selected or a specified
 * template. Returns the persisted generated document ({ pdf: { url, size } }).
 * @param {{restaurantId?: string}} scope
 * @param {string} quoteId
 * @param {{templateId?: string}} [opts]
 * @returns {Promise<object>}
 */
export const renderQuotePdf = async (scope, quoteId, opts = {}) => {
    return apiFetch(`/quotes/${encodeURIComponent(quoteId)}/render`, {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify(opts.templateId ? {templateId: opts.templateId} : {}),
    });
};

/* -------------------------------------------------------------------------- */
/*  Universal Document Gallery                                                  */
/*  Generalizes the quote-template gallery to ANY document entity (invoice,     */
/*  delivery-note, order, quote, contract). The template selection is           */
/*  restaurant-scoped + shared (same `x-restaurant-id` scope as the quotes);    */
/*  the authoritative PDF is always produced by the backend (Puppeteer). Every  */
/*  helper mirrors the quote-template functions above so the feature stays      */
/*  diff-able against its blueprint.                                            */
/* -------------------------------------------------------------------------- */

/**
 * Canonical document entity types the universal gallery supports. Mirrors the
 * backend `entityType` route segment (`/document-gallery/{entityType}/…`).
 * @type {string[]}
 */
export const DOCUMENT_ENTITY_TYPES = ['invoice', 'delivery-note', 'order', 'quote', 'contract'];

/**
 * Map a management detail/list `type` (e.g. `orders`, `invoicesInbound`) onto the
 * canonical document-gallery `entityType`. Passes canonical values through
 * unchanged and falls back to `order` for anything unknown so a bad param can
 * never build a malformed URL.
 * @param {string} type management record type or a canonical entity type.
 * @returns {string} the canonical document entity type.
 */
export const resolveDocumentEntityType = (type) => {
    switch (type) {
        case 'orders':
        case 'order':
            return 'order';
        case 'invoicesInbound':
        case 'invoicesOutbound':
        case 'invoice':
            return 'invoice';
        case 'deliveryNotes':
        case 'delivery-note':
            return 'delivery-note';
        case 'quotes':
        case 'quote':
            return 'quote';
        case 'contracts':
        case 'contract':
            return 'contract';
        default:
            return DOCUMENT_ENTITY_TYPES.includes(type) ? type : 'order';
    }
};

/** URL-safe canonical entity segment for a document-gallery path. */
const documentEntitySegment = (entityType) => encodeURIComponent(resolveDocumentEntityType(entityType));

/**
 * List the document templates visible to the active store for an entity type,
 * grouped into system + custom. Mirrors {@link fetchQuoteTemplates}.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType invoice | delivery-note | order | quote | contract.
 * @param {{locale?: string}} [opts] optional locale override (defaults to the UI language).
 * @returns {Promise<{system: object[], restaurant: object[], locale: string}>}
 */
export const fetchDocumentGallery = async (scope, entityType, opts = {}) => {
    const locale = opts.locale || i18n.language;
    const query = buildQuery({locale});
    const body = await apiFetch(
        `/document-gallery/${documentEntitySegment(entityType)}/templates${query ? `?${query}` : ''}`,
        {headers: scopeHeaders(scope)},
    );

    return {
        system: Array.isArray(body?.system) ? body.system : [],
        restaurant: Array.isArray(body?.restaurant) ? body.restaurant : [],
        locale: body?.locale || locale,
    };
};

/**
 * The store's selected template for an entity type (or the system default).
 * Shared across all users of the store via the `x-restaurant-id` scope header.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType invoice | delivery-note | order | quote | contract.
 * @param {{locale?: string}} [opts] optional locale override.
 * @returns {Promise<object|null>}
 */
export const fetchSelectedDocumentTemplate = async (scope, entityType, opts = {}) => {
    const locale = opts.locale || i18n.language;
    const query = buildQuery({locale});
    const body = await apiFetch(
        `/document-gallery/${documentEntitySegment(entityType)}/selected${query ? `?${query}` : ''}`,
        {headers: scopeHeaders(scope)},
    );

    return body?.template || null;
};

/**
 * Persist the restaurant-scoped selected-template pointer for an entity type.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType invoice | delivery-note | order | quote | contract.
 * @param {string} templateId the template to select.
 * @returns {Promise<object|null>}
 */
export const setSelectedDocumentTemplate = async (scope, entityType, templateId) => {
    const body = await apiFetch(`/document-gallery/${documentEntitySegment(entityType)}/selected`, {
        method: 'PUT',
        headers: scopeHeaders(scope),
        body: JSON.stringify({templateId}),
    });

    return body?.template || null;
};

/**
 * Render a document record to PDF (backend Puppeteer) using the selected or a
 * specified template. Returns the persisted generated document ({ pdf: { url, size } }).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType invoice | delivery-note | order | quote | contract.
 * @param {string} recordId the record to render.
 * @param {{templateId?: string}} [opts] optional explicit template override.
 * @returns {Promise<object>}
 */
export const renderDocumentPdf = async (scope, entityType, recordId, opts = {}) => {
    return apiFetch(
        `/document-gallery/${documentEntitySegment(entityType)}/records/${encodeURIComponent(recordId)}/render`,
        {
            method: 'POST',
            headers: scopeHeaders(scope),
            body: JSON.stringify(opts.templateId ? {templateId: opts.templateId} : {}),
        },
    );
};

/**
 * Generate a brand-new document template from a natural-language prompt (backend
 * AI). The created/draft template is returned so the caller can refresh the
 * gallery and optionally select it.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} entityType invoice | delivery-note | order | quote | contract.
 * @param {{prompt: string, locale?: string, name?: string, category?: string}} payload generation request.
 * @returns {Promise<object>} the created template.
 */
export const generateDocumentTemplate = async (scope, entityType, payload = {}) => {
    return apiFetch(`/document-gallery/${documentEntitySegment(entityType)}/generate-ai`, {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify({
            prompt: payload.prompt || '',
            locale: payload.locale || i18n.language,
            name: payload.name || '',
            category: payload.category || '',
        }),
    });
};

/* -------------------------------------------------------------------------- */
/*  Extended management modules                                                */
/* -------------------------------------------------------------------------- */

// Üzlet beállítások (store settings)
export const fetchRestaurantSettings = async (scope) => {
    if (!scope?.restaurantId) return null;
    const data = await apiFetch(`/restaurant/${scope.restaurantId}`);
    return data?.restaurant || data?.data || data || null;
};

export const updateRestaurantSettings = async (scope, payload) => {
    if (!scope?.restaurantId) throw new Error(i18n.t('management_error_missing_restaurant_id'));
    return apiFetch(`/restaurant/${scope.restaurantId}`, {method: 'PUT', body: JSON.stringify(payload)});
};

/**
 * Create a new product (food) for the active restaurant from the management
 * Termékek list (POST /food, body = FoodForm — the backend requires `name`,
 * `category`, `restaurants` and `available`; all four are sent here). The
 * localized name/short name/description are built from the separate hu/en
 * inputs the create sheet collects (with sane fallbacks for the legacy
 * single-language callers, see QA MGMT-017); a single default size carries
 * the user's chosen unit/price/VAT/barcode — per-size editing happens later
 * through the size editor sheet (see QA MGMT-018).
 * @param {{restaurantId?: string, placeId?: string}} scope active scope.
 * @param {object} input form values.
 * @param {string} [input.nameHu] Hungarian product name (falls back to `input.name`).
 * @param {string} [input.nameEn] English product name (falls back to `input.name`/`nameHu`).
 * @param {string} [input.name] legacy single-language name (back-compat with the old 4-field modal).
 * @param {string} [input.shortNameHu] Hungarian short/display name (falls back to the name).
 * @param {string} [input.shortNameEn] English short/display name (falls back to the name).
 * @param {string} [input.descriptionHu] Hungarian marketing description.
 * @param {string} [input.descriptionEn] English marketing description.
 * @param {string} [input.category] FoodCategory id (required by the backend).
 * @param {string[]} [input.categories] ordered FoodCategory ids (multi-category, first = primary); when present overrides `input.category`, which is still sent as `categories[0]` for backward compatibility.
 * @param {string} [input.foodType] one of the FoodType enum values (default `saleProduct`).
 * @param {string} [input.unit] basic unit of measure (default `db`).
 * @param {number|string} [input.price] default size's gross sale price.
 * @param {number|string} [input.netPrice] default size's net price.
 * @param {number|string} [input.vat] VAT rate percentage (default 27).
 * @param {string} [input.barcode] EAN/UPC assigned to the default size.
 * @param {boolean} [input.availableDineIn] dine-in availability (default true).
 * @param {boolean} [input.availableTakeAway] takeaway availability (default true).
 * @param {boolean} [input.availableOnline] online-order availability (default true).
 * @param {boolean} [input.visible] visible to guests (default true).
 * @param {boolean} [input.enabled] enabled/orderable (default true).
 * @returns {Promise<object>} the created food.
 */
export const createManagementProduct = async (scope, input = {}) => {
    if (!scope?.restaurantId) throw new Error(i18n.t('management_error_missing_restaurant_id'));
    const nameHu = String(input.nameHu || input.name || '').trim();
    const nameEn = String(input.nameEn || input.name || nameHu).trim();
    const shortHu = String(input.shortNameHu || '').trim() || nameHu;
    const shortEn = String(input.shortNameEn || '').trim() || nameEn;
    const price = Number(input.price) || 0;
    const netPrice = Number(input.netPrice);
    const vat = Number(input.vat);
    const barcode = String(input.barcode || '').trim() || undefined;
    const body = {
        restaurant: scope.restaurantId,
        restaurants: [scope.restaurantId],
        name: {hu: nameHu, en: nameEn},
        short_name: {hu: shortHu, en: shortEn},
        ...((input.descriptionHu || input.descriptionEn) ? {description: {hu: input.descriptionHu || '', en: input.descriptionEn || ''}} : {}),
        categories: Array.isArray(input.categories) && input.categories.length ? input.categories : undefined,
        category: (Array.isArray(input.categories) && input.categories.length ? input.categories[0] : input.category) || undefined,
        foodType: input.foodType || 'saleProduct',
        enabled: input.enabled !== false,
        visible: input.visible !== false,
        basicUnit: input.unit || 'db',
        vat: Number.isFinite(vat) ? vat : 27,
        barcode,
        available: {
            dineIn: input.availableDineIn !== false,
            takeAway: input.availableTakeAway !== false,
            online: input.availableOnline !== false,
        },
        sizes: [
            {
                name: {hu: 'Alap', en: 'Default'},
                price,
                netPrice: Number.isFinite(netPrice) ? netPrice : undefined,
                takeAwayPrice: price,
                barcode,
            },
        ],
    };
    return apiFetch('/food', {method: 'POST', body: JSON.stringify(body)});
};

/**
 * Read-only subscription / billing configuration for the active restaurant.
 * Reuses the restaurant detail endpoint (which returns `billingConfig`) and
 * returns just the billing block so the mobile Billing screen can render the
 * plan amount, frequency, payment method, next-payment date and ledger.
 * @param {{restaurantId?: string}} scope
 * @returns {Promise<object|null>} the billingConfig object, or null when unavailable.
 */
export const fetchRestaurantBilling = async (scope) => {
    if (!scope?.restaurantId) return null;
    const data = await apiFetch(`/restaurant/${scope.restaurantId}`);
    const restaurant = data?.restaurant || data?.data || data || null;
    return restaurant?.billingConfig || null;
};

export const fetchManagementIntegrations = async () => normalizeList(await apiFetch('/integrations'), ['integrations']);

// Felhasználókezelés (user management)
export const fetchManagementUsers = async (scope) => normalizeList(await apiFetch('/restaurant/admin', {headers: scopeHeaders(scope)}), ['admins', 'users', 'staff']);

export const inviteManagementUser = async (payload) => apiFetch('/restaurant/invite', {method: 'PUT', body: JSON.stringify(payload)});

export const updateUserRights = async (userId, payload) => apiFetch(`/userRights/${userId}/update`, {method: 'PUT', body: JSON.stringify(payload)});

// Pénzügy (finance)
export const fetchManagementBanks = async (scope) => normalizeList(await apiFetch(`/banks${scope?.restaurantId ? `?restaurantId=${encodeURIComponent(scope.restaurantId)}` : ''}`, {headers: scopeHeaders(scope)}), ['banks']);

export const fetchManagementRegisters = async (scope) => normalizeList(await apiFetch(`/finance/registers${scope?.restaurantId ? `?restaurantId=${encodeURIComponent(scope.restaurantId)}` : ''}`, {headers: scopeHeaders(scope)}), ['registers']);

// CRM
export const fetchManagementQuotes = async (scope) => normalizeList(await apiFetch('/quotes?page=1&limit=50', {headers: scopeHeaders(scope)}), ['quotes']);
// Projektek / feladatok / ügyfélszolgálat (projects, tasks, complaints, support tickets).
// Envelopes: /projects -> {data:{projects}}, /tasks -> {data:{tasks}},
// /complaints -> {data:{complaints}}, /support-tickets -> {items} (all unwrapped by normalizeList).
/**
 * First page (50) of the projects (GET /projects).
 * @param {object} scope active management scope.
 * @param {{ownerId?: string}} [options] `ownerId` narrows SERVER-side to the projects owned by that user.
 * @returns {Promise<object[]>} the project rows.
 */
export const fetchManagementProjects = async (scope, options = {}) => {
    const owner = options.ownerId ? `&${buildQuery({ownerId: options.ownerId})}` : '';

    return normalizeList(await apiFetch(`/projects?page=1&limit=50&sortBy=createdAt&sortOrder=desc${owner}`, {headers: scopeHeaders(scope)}), ['projects']);
};

/**
 * First page (50) of the tasks (GET /tasks).
 * @param {object} scope active management scope.
 * @param {{assignee?: string}} [options] `assignee` = a user id, or `null` for the tasks nobody is assigned to (SERVER-side).
 * @returns {Promise<object[]>} the task rows.
 */
export const fetchManagementTasks = async (scope, options = {}) => {
    const assignee = options.assignee ? `&${buildQuery({assignee: options.assignee})}` : '';

    return normalizeList(await apiFetch(`/tasks?page=1&limit=50&sortBy=createdAt&sortOrder=desc${assignee}`, {headers: scopeHeaders(scope)}), ['tasks']);
};
export const fetchManagementComplaints = async (scope) => normalizeList(await apiFetch('/complaints?page=1&limit=50&sortBy=createdAt&sortOrder=desc', {headers: scopeHeaders(scope)}), ['complaints']);
export const fetchManagementSupportTickets = async (scope) => normalizeList(await apiFetch('/support-tickets?page=1&limit=50&sort=-createdAt', {headers: scopeHeaders(scope)}), ['items', 'tickets']);
export const fetchManagementCrmGroups = async (scope) => normalizeList(await apiFetch('/crm/groups', {headers: scopeHeaders(scope)}), ['groups']);
export const fetchManagementSalespeople = async (scope) => normalizeList(await apiFetch('/crm/salespeople', {headers: scopeHeaders(scope)}), ['salespeople']);

// Szerviz (service)
export const fetchManagementTechnicians = async (scope) => normalizeList(await apiFetch('/serviceModule/technicians?limit=50', {headers: scopeHeaders(scope)}), ['technicians']);
export const fetchManagementAppointments = async (scope) => normalizeList(await apiFetch('/serviceModule/appointments?limit=50', {headers: scopeHeaders(scope)}), ['appointments']);

// ERP / logisztika
export const fetchManagementWarehouses = async (scope) => sortWarehousesByName(normalizeList(await apiFetch(`/warehouse/warehouses${scope?.restaurantId ? `?restaurantId=${encodeURIComponent(scope.restaurantId)}` : ''}`), ['warehouses']));
export const fetchManagementTransactions = async (scope) => {
    if (!scope?.restaurantId) return [];
    return normalizeList(await apiFetch(`/warehouse/getRestaurantTransactions?restaurantId=${encodeURIComponent(scope.restaurantId)}&page=1&limit=50`), ['transactions']);
};

/* -------------------------------------------------------------------------- */
/*  Mutations — wired to the real backend endpoints (see admin/swagger.json)    */
/* -------------------------------------------------------------------------- */

const idOf = (item) => item?._id || item?.id || item?.workOrderNumber || item?.invoiceId;

/**
 * Reshape the flat user-detail form payload into the exact shape the `User`
 * Mongoose schema expects, so admin edits actually persist.
 *
 * The management detail screen edits users with flat scalar fields (`name`,
 * `role`), but the backend `User` model stores `name` as a nested
 * `{ first, last }` object and roles as a `roles: [String]` array (there is no
 * `role` field). Because `User` uses Mongoose strict mode, a flat `name` string
 * and an unknown `role` key are silently stripped on `$set` — the request
 * returns 200 ("Saved successfully") while the name/role never change. We map
 * them here so `PUT /admin/user/:id` writes fields the schema recognises.
 *
 * - `name` (string) → `{ first, last }` (first token vs. the rest), preserving
 *   any existing nested name object the user did not touch.
 * - `role` (string) → `roles` array: the chosen role replaces the primary slot
 *   (`roles[0]`) while any additional roles the user already holds are kept.
 *
 * @param {object} payload Raw diff payload from the detail form.
 * @param {object} item    Original user record (for preserving untouched roles).
 * @returns {object} Schema-shaped payload safe to send to the backend.
 */
export const shapeUserUpdatePayload = (payload = {}, item = {}) => {
    const out = {...payload};

    if (Object.prototype.hasOwnProperty.call(out, 'name')) {
        const raw = out.name;
        if (typeof raw === 'string') {
            const parts = raw.trim().split(/\s+/).filter(Boolean);
            out.name = {
                first: parts.length ? parts[0] : '',
                last: parts.length > 1 ? parts.slice(1).join(' ') : '',
            };
        }
    }

    if (Object.prototype.hasOwnProperty.call(out, 'role')) {
        const chosen = out.role;
        delete out.role; // phantom field — schema has no `role`, only `roles[]`
        const existing = Array.isArray(item.roles) ? item.roles.slice() : [];
        if (chosen) {
            existing[0] = chosen;
            out.roles = existing.filter(Boolean);
        }
    }

    return out;
};

/**
 * Persist edited fields for a management record. Maps each module type to the
 * exact backend endpoint that the admin app uses.
 */
export const updateManagementRecord = async (type, item, payload) => {
    const id = idOf(item);
    if (!id) throw new Error(i18n.t('management_error_missing_id_save'));

    // The serviceModule/* write endpoints match on `{ _id, restaurantId }` where
    // restaurantId is resolved from the `x-restaurant-id` header. Without it a
    // multi-store manager editing a record that belongs to a non-primary store
    // gets a silent 404 ("record not found"). Resolve the active scope so the
    // edit lands on the correct store.
    const svc = {headers: scopeHeaders(await getManagementScope())};

    switch (type) {
        case 'leads':
            return apiFetch(`/crm/leads/${id}`, {method: 'PUT', body: JSON.stringify(payload)});
        case 'costTypes':
            return apiFetch(`/costTypes/${id}`, {method: 'PUT', body: JSON.stringify(payload)});
        case 'workOrders':
            return apiFetch(`/serviceModule/work-orders/${id}`, {method: 'PATCH', body: JSON.stringify(payload), ...svc});
        case 'products':
            return apiFetch(`/food/${id}`, {method: 'PUT', body: JSON.stringify(payload)});
        case 'orders':
            return apiFetch(`/order/${id}/update`, {method: 'POST', body: JSON.stringify(payload)});
        case 'invoicesInbound':
        case 'invoicesOutbound':
            return apiFetch(`/navInvoices/${id}/edit`, {method: 'PUT', body: JSON.stringify(payload)});
        case 'users':
            return apiFetch(`/admin/user/${id}`, {method: 'PUT', body: JSON.stringify(shapeUserUpdatePayload(payload, item))});
        case 'banks':
            return apiFetch(`/banks/${id}`, {method: 'PUT', body: JSON.stringify(payload)});
        case 'expenses':
            return apiFetch(`/expenses/${id}`, {method: 'PUT', body: JSON.stringify(payload)});
        case 'quotes':
            return apiFetch(`/quotes/${id}`, {method: 'PUT', body: JSON.stringify(payload)});
        case 'technicians':
            return apiFetch(`/serviceModule/technicians/${id}`, {method: 'PUT', body: JSON.stringify(payload), ...svc});
        case 'appointments':
            return apiFetch(`/serviceModule/appointments/${id}`, {method: 'PATCH', body: JSON.stringify(payload), ...svc});
        case 'customers':
            // ServiceModuleCustomer update is PATCH (not PUT) and transparently
            // falls back to the legacy Customer collection server-side.
            return apiFetch(`/serviceModule/customers/${id}`, {method: 'PATCH', body: JSON.stringify(payload), ...svc});
        case 'unifiedCustomers':
            // The unified facade resolves the id across the store/partner/guest/CRM
            // collections itself, so ONE PATCH covers every source — but only when
            // the source is told apart: `type` (`restaurant` | `serviceModule`, as
            // normalized onto every unified-customer read) MUST ride in the body,
            // else the backend's own auto-detect can resolve the WRONG collection
            // for an id that collides across Customer/ServiceModuleCustomer and the
            // write silently lands nowhere. Order-derived guests are read-only there
            // and answer 409 (not 404) — surface that as "convert the guest first",
            // never as a generic save failure.
            return apiFetch(`/customers/unified/${id}`, {method: 'PATCH', body: JSON.stringify({type: item.type, ...payload}), ...svc});
        case 'warehouses':
            // The mutation route is the singular /warehouse/{id} prefix — there is
            // NO /warehouse/warehouses/{id} update endpoint on the backend.
            return apiFetch(`/warehouse/${id}`, {method: 'PUT', body: JSON.stringify(payload)});
        default:
            throw new Error(i18n.t('management_error_no_edit_endpoint'));
    }
};

/** Change the status of a record using the dedicated status endpoints. */
export const updateManagementStatus = async (type, item, status) => {
    const id = idOf(item);
    if (!id) throw new Error(i18n.t('management_error_missing_id_status'));

    // Same store-scoping requirement as updateManagementRecord: the serviceModule
    // status endpoints resolve the restaurant from the `x-restaurant-id` header.
    const svc = {headers: scopeHeaders(await getManagementScope())};

    switch (type) {
        case 'orders':
            return apiFetch(`/order/${id}/status/${encodeURIComponent(status)}`);
        case 'workOrders':
            return apiFetch(`/serviceModule/work-orders/${id}/status`, {method: 'PATCH', body: JSON.stringify({status}), ...svc});
        case 'invoicesInbound':
        case 'invoicesOutbound':
            return apiFetch(`/navInvoices/${id}/status/${encodeURIComponent(status)}`, {method: 'PUT'});
        case 'products':
            return apiFetch(`/food/${status === 'enabled' ? 'enable' : 'disable'}/${id}`);
        case 'appointments':
            return apiFetch(`/serviceModule/appointments/${id}/status`, {method: 'PATCH', body: JSON.stringify({status}), ...svc});
        case 'technicians':
            return apiFetch(`/serviceModule/technicians/${id}/status`, {method: 'POST', body: JSON.stringify({status}), ...svc});
        default:
            throw new Error(i18n.t('management_error_no_status_endpoint'));
    }
};

/** Order-specific actions used on the order detail screen. */
export const sendOrderInvoice = async (item) => apiFetch(`/order/${idOf(item)}/sendInvoice`, {method: 'POST', body: JSON.stringify({})});
export const stornoOrder = async (item) => apiFetch(`/order/${idOf(item)}/storno`, {method: 'POST', body: JSON.stringify({})});

/**
 * Add a note/comment to a lead's timeline (POST /crm/leads/{id}/timeline).
 * The backend TimelineEvent expects `{event, description}` (NOT `{note}` — the
 * model has no such field), and responds 201 with BOTH the repopulated lead and
 * the created entry: `{lead, timelineEvent}`. `note` is still sent alongside
 * for backward compatibility with older payload readers.
 * @param {object} item the lead record.
 * @param {string} note the free-text comment.
 * @returns {Promise<{lead: object, timelineEvent: object}>}
 */
export const addLeadTimelineNote = async (item, note) => apiFetch(`/crm/leads/${idOf(item)}/timeline`, {
    method: 'POST',
    body: JSON.stringify({event: 'comment', description: note, note}),
});
export const addInvoiceNote = async (item, note) => apiFetch(`/navInvoices/${idOf(item)}/notes`, {method: 'POST', body: JSON.stringify({text: note})});
export const deleteManagementInvoice = async (item, reason = '') => apiFetch(`/navInvoices/${idOf(item)}/delete`, {method: 'DELETE', body: JSON.stringify(reason ? {reason} : {})});
export const restoreManagementInvoice = async (item) => apiFetch(`/navInvoices/${idOf(item)}/restore`, {method: 'PUT'});
export const reopenManagementInvoice = async (item) => apiFetch(`/navInvoices/${idOf(item)}/reopen`, {method: 'PUT'});
export const addManagementInvoicePayment = async (item, payload) => apiFetch(`/navInvoices/${idOf(item)}/payments`, {method: 'POST', body: JSON.stringify(payload)});

/** Type-level capability flags so the detail screen knows what is editable. */
export const isManagementTypeEditable = (type) => [
    'leads', 'costTypes', 'workOrders', 'products', 'orders', 'invoicesInbound', 'invoicesOutbound',
    'users', 'banks', 'quotes', 'technicians', 'appointments', 'customers', 'unifiedCustomers', 'warehouses',
    'expenses',
].includes(type);

/* -------------------------------------------------------------------------- */
/*  Create + module-action endpoints (verified against the backend swagger)     */
/* -------------------------------------------------------------------------- */

/**
 * Create a CRM lead (POST /crm/leads). The backend requires at least a `name`
 * or a `company` (400 otherwise), auto-creates the initial "Lead Created"
 * timeline event and notifies the assignee. Returns the populated Lead.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload `{name, company, email, phone, status, source, notes, ...}`.
 * @returns {Promise<object>} the created (populated) Lead.
 */
export const createManagementLead = async (scope, payload) => apiFetch('/crm/leads', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Change a lead's pipeline status via the DEDICATED status route
 * (PUT /crm/leads/{id}/status) instead of a whole-record PUT — the backend
 * validates the status against LEAD_STATUSES and appends a timeline event.
 * @param {object} item the lead record (id resolved via `_id`/`id`).
 * @param {string} status one of the backend LEAD_STATUSES
 *   (new | contacted | demo-scheduled | quote | interested | not-interested | converted).
 * @param {string} [note] optional note stored with the status change.
 * @returns {Promise<object>} the updated (populated) Lead.
 */
export const updateLeadStatusDirect = async (item, status, note) => apiFetch(`/crm/leads/${idOf(item)}/status`, {
    method: 'PUT',
    body: JSON.stringify(note ? {status, note} : {status}),
});

/**
 * Set a lead's status through the dedicated route, either a pipeline status
 * (`{status}`, clears the custom label) or a custom label (`{customStatusKey}`).
 * 400 = unknown custom key, 409 = lead already converted.
 * Called over raw HTTP (not swaggerClient) — the route is newer than the prod spec.
 * @param {object} item the lead record.
 * @param {{status?: string, customStatusKey?: string, note?: string}} body request body.
 * @returns {Promise<object>} the updated Lead.
 */
export const setLeadStatus = async (item, body) => apiFetch(`/crm/leads/${idOf(item)}/status`, {
    method: 'PUT',
    body: JSON.stringify(body),
});

/**
 * Load the caller's lead quick-status preferences (GET /crm/lead-status-preferences).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<object>} `{quickStatuses, customStatuses, pipelineStatuses, isDefault}`.
 */
export const fetchLeadStatusPreferences = async (scope) => apiFetch('/crm/lead-status-preferences', {headers: scopeHeaders(scope)});

/**
 * Save the caller's lead quick-status preferences (PUT /crm/lead-status-preferences).
 * The server-normalized response is the new state.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {{quickStatuses: string[], customStatuses: object[]}} payload preferences.
 * @returns {Promise<object>} normalized preferences.
 */
export const saveLeadStatusPreferences = async (scope, payload) => apiFetch('/crm/lead-status-preferences', {
    method: 'PUT',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Convert a lead to a CRM customer (POST /crm/leads/{id}/convert). For a
 * normal (non-main-tenant) manager the backend upserts a legacy `Customer`
 * scoped to the active restaurant; the lead must resolve an email (else 400).
 * The `x-restaurant-id` header decides WHICH store the customer lands in.
 * @param {object} item the lead record.
 * @param {object} [payload] optional body (e.g. `{email}` when the lead has none).
 * @returns {Promise<object>} `{id, name, email, conversionTarget, customer, ...}`.
 */
export const convertManagementLead = async (item, payload = {}) => {
    const svc = {headers: scopeHeaders(await getManagementScope())};

    return apiFetch(`/crm/leads/${idOf(item)}/convert`, {method: 'POST', body: JSON.stringify(payload), ...svc});
};

/**
 * Apply the same field updates to multiple leads at once (PUT /crm/leads/bulk),
 * limited to leads within the caller's current scope. This is the ONLY route
 * that accepts `lostReason` — the dedicated status endpoint
 * ({@link updateLeadStatusDirect}) silently ignores it.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string[]} leadIds ids of the leads to update.
 * @param {object} updates shared field updates to apply (e.g. `{status, lostReason}`).
 * @returns {Promise<{success: boolean, updatedCount: number, updatedLeadIds: string[]}>}
 */
export const bulkUpdateLeads = async (scope, leadIds, updates) => apiFetch('/crm/leads/bulk', {
    method: 'PUT',
    headers: scopeHeaders(scope),
    body: JSON.stringify({leadIds, updates}),
});

/**
 * Soft delete one or more leads (POST /crm/leads/bulk-delete) — hidden from
 * listings but recoverable in the database. There is NO single-lead DELETE
 * route; a single id is sent as a one-element array.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string[]} leadIds ids of the leads to soft delete.
 * @returns {Promise<{success: boolean, deletedCount: number}>}
 */
export const bulkDeleteLeads = async (scope, leadIds) => apiFetch('/crm/leads/bulk-delete', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify({leadIds}),
});

/**
 * Mark a single lead as lost with a reason ("Mark as lost" action). Routes
 * through {@link bulkUpdateLeads} (with a one-element id array) rather than
 * {@link updateLeadStatusDirect}, because only the bulk route persists
 * `lostReason`.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the lead record.
 * @param {string} lostReason free-text reason the lead was lost.
 * @returns {Promise<{success: boolean, updatedCount: number, updatedLeadIds: string[]}>}
 */
export const markLeadLost = async (scope, item, lostReason) => bulkUpdateLeads(
    scope,
    [idOf(item)],
    {status: 'not-interested', lostReason},
);

/**
 * Reopen a lost/converted lead by moving it back to the `contacted` pipeline
 * stage ("Reopen" action). Uses the dedicated status route directly — no
 * `lostReason` needs to travel with a reopen.
 * @param {object} item the lead record.
 * @returns {Promise<object>} the updated (populated) Lead.
 */
export const reopenLead = async (item) => updateLeadStatusDirect(item, 'contacted');

/**
 * Fallback for uploading a document onto a lead when the generic attachments
 * route ({@link module:services/entityDocumentsApi.uploadEntityAttachment})
 * 404s: read-modify-write the embedded `Lead.documents` array
 * (PUT /crm/leads/{id}) with the new entry carrying a base64 `data` payload —
 * the backend uploads it to Spaces/R2 itself and rewrites the entry with a `url`.
 * @param {object} item the lead record (reads the current `documents` array).
 * @param {{fileName: string, mimeType?: string, size?: number, contentBase64: string}} file
 *   the picked file, already read into base64 by the caller (kept out of this
 *   pure helper so it stays free of the `expo-file-system` native dependency).
 * @returns {Promise<object>} the updated (populated) Lead.
 */
export const uploadLeadDocumentFallback = async (item, file) => {
    const existing = Array.isArray(item?.documents) ? item.documents : [];
    const entry = {
        fileName: file.fileName,
        mimeType: file.mimeType || 'application/octet-stream',
        size: file.size || 0,
        data: file.contentBase64,
    };

    return updateManagementRecord('leads', item, {documents: [...existing, entry]});
};

/**
 * Fallback for deleting a lead document when the generic attachment DELETE
 * route ({@link module:services/entityDocumentsApi.deleteEntityAttachment})
 * 404s: flag the matching embedded document `deleted: true` and PUT the whole
 * array back (PUT /crm/leads/{id}) — there is no per-document DELETE on the
 * legacy embedded array, only a soft-delete flag.
 * @param {object} item the lead record (reads the current `documents` array).
 * @param {object} doc the embedded document to remove — matched by `_id` when
 *   present, otherwise by `storageKey`, otherwise by `fileName` + `url`.
 * @returns {Promise<object>} the updated (populated) Lead.
 */
export const deleteLeadDocumentFallback = async (item, doc) => {
    const existing = Array.isArray(item?.documents) ? item.documents : [];
    const matches = (d) => (doc._id && d._id && String(d._id) === String(doc._id))
        || (!doc._id && doc.storageKey && d.storageKey === doc.storageKey)
        || (!doc._id && !doc.storageKey && d.fileName === doc.fileName && d.url === doc.url);
    const next = existing.map((d) => (matches(d) ? {...d, deleted: true} : d));

    return updateManagementRecord('leads', item, {documents: next});
};

/**
 * Create a service-module customer (POST /serviceModule/customers) — the
 * `ServiceModuleCustomer` collection, NOT the CRM Lead / legacy Customer ones.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload `{customerType, name:{first,last}, company:{name,taxId,...}, contact:{phone,...}}`.
 * @returns {Promise<object>} the created customer.
 */
export const createManagementCustomer = async (scope, payload) => apiFetch('/serviceModule/customers', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Update a service-module customer (PATCH /serviceModule/customers/{id}).
 * PATCH — not PUT — and the backend transparently falls back to the legacy
 * `Customer` collection when the id resolves there instead.
 * @param {object} item the customer record.
 * @param {object} payload partial customer fields.
 * @returns {Promise<object>} the updated customer.
 */
export const updateManagementCustomer = async (item, payload) => {
    const svc = {headers: scopeHeaders(await getManagementScope())};

    return apiFetch(`/serviceModule/customers/${idOf(item)}`, {method: 'PATCH', body: JSON.stringify(payload), ...svc});
};

/**
 * Create a customer through the UNIFIED facade (POST /customers/unified). The
 * backend always persists it as a Service Module (CRM) customer — the same
 * collection {@link createManagementCustomer} writes — but takes the FLAT body
 * the unified contract documents (`firstName`/`lastName`/`companyName`/`email`/
 * `phone`/`taxNumber`/…), NOT the nested `name{first,last}` / `company{}` /
 * `contact{}` shape of `POST /serviceModule/customers`. Answers 201.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload flat unified-customer body.
 * @returns {Promise<object>} `{success, type, data, raw}` — the created customer.
 */
export const createUnifiedCustomer = async (scope, payload) => apiFetch(
    `/customers/unified${scope?.restaurantId ? `?restaurantId=${encodeURIComponent(scope.restaurantId)}` : ''}`,
    {method: 'POST', headers: scopeHeaders(scope), body: JSON.stringify(payload)},
);

/**
 * Duplicate a product (PUT /food/{foodId}/copy — note: the verified route is a
 * PUT with the `/copy` SUFFIX, not `POST /food/copy/{id}`). The backend clones
 * the Food doc, strips size ids and appends " (2)" to the name; no body needed.
 * @param {object} item the food record to duplicate.
 * @returns {Promise<object>} the newly created Food document.
 */
export const copyManagementFood = async (item) => apiFetch(`/food/${idOf(item)}/copy`, {method: 'PUT'});

/**
 * Load the option list for the product extras (topping-category) picker —
 * the SAME full FoodCategory list admin uses
 * (GET /restaurant/{restaurantId}/categories); there is no server-side
 * isExtra-only endpoint, the picker filters/matches by id client-side.
 * Normalized to `[{_id, name, isExtra}]` with `name` resolved to a display
 * string (hu → en → raw).
 * @param {{restaurantId?: string}} [scope] active scope (resolved from storage when omitted).
 * @returns {Promise<Array<{_id: string, name: string, isExtra: boolean}>>}
 */
export const fetchFoodExtraOptions = async (scope) => {
    const resolved = scope || await getManagementScope();
    if (!resolved?.restaurantId) return [];
    const list = normalizeList(
        await apiFetch(`/restaurant/${resolved.restaurantId}/categories?restaurantId=${encodeURIComponent(resolved.restaurantId)}`),
        ['categories'],
    );

    return list
        .map((cat) => ({
            _id: String(cat?._id || cat?.id || ''),
            name: String(cat?.name?.hu || cat?.name?.en || cat?.name || ''),
            isExtra: Boolean(cat?.isExtra),
        }))
        .filter((cat) => cat._id);
};

/**
 * Create a cost type (POST /costTypes) scoped to the active restaurant.
 * The CostType model requires the scope in a `restaurant` field (the body is
 * passed straight to `new CostType()` with no restaurantId→restaurant mapping),
 * so BOTH keys are sent — `restaurant` for the model, `restaurantId` kept for
 * backward compatibility with any middleware reading it.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload `{name, description?, rootType?}`.
 * @returns {Promise<object>} the created cost type.
 */
export const createManagementCostType = async (scope, payload) => apiFetch('/costTypes', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify({
        ...payload,
        ...(scope?.restaurantId ? {restaurant: scope.restaurantId, restaurantId: scope.restaurantId} : {}),
    }),
});

/**
 * Create a bank account (POST /banks) scoped to the active restaurant.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload `{name, accountNumber?, iban?, currency?}`.
 * @returns {Promise<object>} the created bank.
 */
export const createManagementBank = async (scope, payload) => apiFetch('/banks', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify({...payload, ...(scope?.restaurantId ? {restaurantId: scope.restaurantId} : {})}),
});

/**
 * Soft-delete a bank account (DELETE /banks/{bankId}). The endpoint answers
 * `204 No Content` on success, so this goes through {@link apiFetchAllowEmpty}
 * rather than `apiFetch` (which would throw parsing the empty body).
 * @param {object} item the bank record.
 * @returns {Promise<null>} always `null` (no response body).
 */
export const deleteManagementBank = async (item) => apiFetchAllowEmpty(`/banks/${idOf(item)}`, {method: 'DELETE'});

/**
 * Soft-delete a cost type (DELETE /costTypes/{id}). Historical expenses keep
 * their categorisation — the document is retained with `deleted: true`.
 * @param {object} item the cost type record.
 * @returns {Promise<{success: boolean, message: string}>} the status envelope.
 */
export const deleteManagementCostType = async (item) => apiFetch(`/costTypes/${idOf(item)}`, {method: 'DELETE'});

/**
 * Seed the starter set of NAV-aligned cost types for a store
 * (POST /costTypes/generate?restaurantId=). A no-op (200, existing set
 * returned) when the store already has cost types — safe to call repeatedly.
 * @param {{restaurantId?: string}} scope active management scope.
 * @returns {Promise<{success: boolean, message: string, costTypes: object[]}>}
 */
export const generateManagementCostTypes = async (scope) => apiFetch(
    `/costTypes/generate?restaurantId=${encodeURIComponent(scope?.restaurantId || '')}`,
    {method: 'POST', headers: scopeHeaders(scope)},
);

/**
 * Create a manual operating expense (POST /expenses). `name` and `amount`
 * are required by the backend; `restaurant` is sent explicitly (mirrors
 * {@link createManagementCostType}) so a multi-store manager's expense always
 * lands on the SELECTED store, not the caller's primary one.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload `{name, amount, category?, costType?, costTypeName?, vendor?, date?, status?, notes?}`.
 * @returns {Promise<object>} the created expense.
 */
export const createManagementExpense = async (scope, payload) => apiFetch('/expenses', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify({...payload, ...(scope?.restaurantId ? {restaurant: scope.restaurantId} : {})}),
});

/**
 * Soft-delete an expense (DELETE /expenses/{id}).
 * @param {object} item the expense record.
 * @returns {Promise<{success: boolean, message: string}>} the status envelope.
 */
export const deleteManagementExpense = async (item) => apiFetch(`/expenses/${idOf(item)}`, {method: 'DELETE'});

/**
 * Cash-register open/closed states the finance API can answer with. The
 * `finance.swagger.yaml` contract documents `204`/`226` on the single-register
 * GET ("not active"/"already opened"), and the live `openRegister`/
 * `closeRegister` mutations additionally answer `406`/`409` when the register
 * is already in the requested state — every one of these is a legitimate
 * register state, not a failure, so {@link openManagementRegister} and
 * {@link closeManagementRegister} normalize all four into an informational
 * result instead of throwing.
 * @type {number[]}
 */
const REGISTER_CONFLICT_STATUSES = [204, 226, 406, 409];

/**
 * Open a cash register for the current shift (PUT /finance/registers/{id}/open).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the register record (floor-plan element, `_id`).
 * @param {{amount?: number, comment?: string}} [payload] opening float + note.
 * @returns {Promise<{ok: boolean, alreadyOpen?: boolean, register?: object}>}
 */
export const openManagementRegister = async (scope, item, payload = {}) => {
    try {
        const body = await apiFetch(`/finance/registers/${idOf(item)}/open`, {
            method: 'PUT',
            headers: scopeHeaders(scope),
            body: JSON.stringify({amount: Number(payload.amount) || 0, comment: payload.comment || ''}),
        });
        return {ok: true, register: body?.register || body};
    } catch (err) {
        if (REGISTER_CONFLICT_STATUSES.includes(parseApiErrorStatus(err))) {
            return {ok: false, alreadyOpen: true};
        }
        throw err;
    }
};

/**
 * Close a cash register at the end of a shift (PUT /finance/registers/{id}/close).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the register record (floor-plan element, `_id`).
 * @param {{amount?: number, comment?: string}} [payload] closing amount + note.
 * @returns {Promise<{ok: boolean, alreadyClosed?: boolean, register?: object}>}
 */
export const closeManagementRegister = async (scope, item, payload = {}) => {
    try {
        const body = await apiFetch(`/finance/registers/${idOf(item)}/close`, {
            method: 'PUT',
            headers: scopeHeaders(scope),
            body: JSON.stringify({amount: Number(payload.amount) || 0, comment: payload.comment || ''}),
        });
        return {ok: true, register: body?.register || body};
    } catch (err) {
        if (REGISTER_CONFLICT_STATUSES.includes(parseApiErrorStatus(err))) {
            return {ok: false, alreadyClosed: true};
        }
        throw err;
    }
};

/**
 * Line-item math for a standalone invoice: net/VAT/gross per line and the
 * aggregated invoice totals — a PURE function (no network) so it stays
 * jest-testable in isolation from {@link createManagementInvoice}.
 * @param {Array<{quantity?: number, unitPrice?: number, vatRate?: number}>} items raw line-item drafts.
 * @returns {{items: object[], totals: {netAmount: number, vatAmount: number, grossAmount: number}}}
 *   the items shaped for the wire (rounded to 2 decimals) plus the summed totals.
 */
export const computeInvoiceTotals = (items = []) => {
    const shaped = (Array.isArray(items) ? items : []).map((raw, index) => {
        const quantity = Number(raw.quantity) || 0;
        const unitPrice = Number(raw.unitPrice) || 0;
        const vatRate = Number(raw.vatRate) || 0;
        const netAmount = Number((quantity * unitPrice).toFixed(2));
        const vatAmount = Number(((netAmount * vatRate) / 100).toFixed(2));
        const grossAmount = Number((netAmount + vatAmount).toFixed(2));

        return {
            id: raw.id ?? index + 1,
            description: raw.description || '',
            unit: raw.unit || undefined,
            quantity,
            unitPrice,
            vatRate,
            netAmount,
            vatAmount,
            grossAmount,
        };
    });

    const totals = shaped.reduce((sum, line) => ({
        netAmount: Number((sum.netAmount + line.netAmount).toFixed(2)),
        vatAmount: Number((sum.vatAmount + line.vatAmount).toFixed(2)),
        grossAmount: Number((sum.grossAmount + line.grossAmount).toFixed(2)),
    }), {netAmount: 0, vatAmount: 0, grossAmount: 0});

    return {items: shaped, totals};
};

/**
 * Build the exact body `POST /invoices/create` expects from the invoice
 * editor's form values — a PURE function (no network) so the payload shape
 * and the totals math stay jest-testable without mocking `fetch`.
 * @param {object} values form values: `{sellerName, sellerTaxNumber, buyerName?,
 *   buyerTaxNumber?, issueDate, deliveryDate?, paymentDueDate, invoiceNumber?,
 *   currency, direction, invoiceType?, originalInvoiceNumber?, modificationReason?}`.
 * @param {Array<{description?: string, quantity?: number, unitPrice?: number, vatRate?: number}>} items line items.
 * @returns {object} the request body for {@link createManagementInvoice}.
 */
export const buildInvoiceCreatePayload = (values = {}, items = []) => {
    const {items: shapedItems, totals} = computeInvoiceTotals(items);

    return {
        sellerName: values.sellerName || '',
        sellerTaxNumber: values.sellerTaxNumber || '',
        buyerName: values.buyerName || undefined,
        buyerTaxNumber: values.buyerTaxNumber || undefined,
        issueDate: values.issueDate,
        deliveryDate: values.deliveryDate || values.issueDate,
        paymentDueDate: values.paymentDueDate,
        invoiceNumber: values.invoiceNumber || undefined,
        currency: values.currency || 'HUF',
        direction: values.direction === 'INBOUND' ? 'INBOUND' : 'OUTBOUND',
        invoiceType: values.invoiceType || 'manual_no_nav',
        originalInvoiceNumber: values.originalInvoiceNumber || undefined,
        modificationReason: values.modificationReason || undefined,
        items: shapedItems,
        totals,
    };
};

/**
 * Create a standalone invoice (POST /invoices/create) — the creation path is
 * a DIFFERENT endpoint from the NAV invoice list/detail (`/navInvoices/*`).
 * `nav`/`advance`/`correction`/`storno` also submit to NAV; the default
 * `manual_no_nav` records the invoice locally only.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload built by {@link buildInvoiceCreatePayload}.
 * @returns {Promise<object>} the confirmation envelope `{success, message}`.
 */
export const createManagementInvoice = async (scope, payload) => apiFetch('/invoices/create', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Create a quote (POST /quotes). The backend generates the `quoteNumber`
 * itself (generateServeeId), so the payload only carries the business fields.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload `{customerName, customerEmail?, description?, items?, ...}`.
 * @returns {Promise<object>} the created quote.
 */
export const createManagementQuote = async (scope, payload) => apiFetch('/quotes', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Create a work order (POST /serviceModule/work-orders). Requires an existing
 * `customerId` (the schema ref is required); `workOrderNumber` is generated
 * server-side when omitted. For "new customer inline" flows use
 * {@link createManagementCompositeWorkOrder} instead.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload work-order fields (customerId, description, ...).
 * @returns {Promise<object>} the created work order.
 */
export const createManagementWorkOrder = async (scope, payload) => apiFetch('/serviceModule/work-orders', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Create a work order together with its (new) customer/vehicle in one call
 * (POST /serviceModule/work-orders/composite). The backend creates the
 * ServiceModuleCustomer / vehicle Asset first and links their ids.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {{customer?: object, vehicle?: object, workOrder: object}} payload composite body.
 * @returns {Promise<object>} the created work order.
 */
export const createManagementCompositeWorkOrder = async (scope, payload) => apiFetch('/serviceModule/work-orders/composite', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Create a technician (POST /serviceModule/technicians — service_v2 route).
 * `firstName` + `lastName` are required by the backend; email/phone/roles are
 * optional. Returns the created User doc (201).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload `{firstName, lastName, email?, phone?, roles?}`.
 * @returns {Promise<object>} the created technician user.
 */
export const createManagementTechnician = async (scope, payload) => apiFetch('/serviceModule/technicians', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Create a service appointment (POST /serviceModule/appointments). Only
 * `dateFrom` is required; title/notes/customerId/technicianId are optional.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} payload `{dateFrom, dateTo?, title?, notes?, customerId?, technicianId?}`.
 * @returns {Promise<object>} the created appointment.
 */
export const createManagementAppointment = async (scope, payload) => apiFetch('/serviceModule/appointments', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Update a warehouse (PUT /warehouse/{warehouseId}). Verified: the update
 * lives under the singular `/warehouse/{id}` prefix, NOT `/warehouse/warehouses/{id}`.
 * @param {object} item the warehouse record.
 * @param {object} payload partial fields `{name?, default?, manufacturingDefault?, enabled?}`.
 * @returns {Promise<object>} the updated warehouse.
 */
export const updateManagementWarehouse = async (item, payload) => apiFetch(`/warehouse/${idOf(item)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
});

/* -------------------------------------------------------------------------- */
/*  Date range presets                                                          */
/* -------------------------------------------------------------------------- */
export const MANAGEMENT_RANGE_PRESETS = [
    {value: 7, label: i18n.t('management_range_preset_7_days')},
    {value: 30, label: i18n.t('management_range_preset_30_days')},
    {value: 90, label: i18n.t('management_range_preset_90_days')},
];

/* -------------------------------------------------------------------------- */
/*  Live auto-refresh interval (management background polling)                  */
/* -------------------------------------------------------------------------- */

/** persistentSettings key holding the user's chosen management refresh interval (ms). */
export const MANAGEMENT_REFRESH_INTERVAL_KEY = 'mgmt_refresh_interval_ms';

/** Default management auto-refresh cadence when the user has no saved preference. */
export const MANAGEMENT_REFRESH_DEFAULT_MS = 5000;

/**
 * Selectable auto-refresh cadences for the management dashboard / orders list.
 * `value` is the poll interval in milliseconds; `0` means "Never" (polling off).
 * Labels are i18n keys resolved at render time (not at module load) so they
 * stay correct after a runtime language switch. The `labelKey`s live in every
 * mobile locale (hu/en/de/cn/ro/svn).
 * @type {Array<{value: number, labelKey: string}>}
 */
export const MANAGEMENT_REFRESH_PRESETS = [
    {value: 5000, labelKey: 'mgmt_refresh_5s'},
    {value: 15000, labelKey: 'mgmt_refresh_15s'},
    {value: 30000, labelKey: 'mgmt_refresh_30s'},
    {value: 60000, labelKey: 'mgmt_refresh_1m'},
    {value: 300000, labelKey: 'mgmt_refresh_5m'},
    {value: 0, labelKey: 'mgmt_refresh_never'},
];

/**
 * Normalize a raw (possibly persisted/string) refresh-interval value into a safe
 * millisecond cadence. Unknown/blank/NaN values fall back to the default; `0`
 * (or a clean "never") disables polling; out-of-range positives are clamped to
 * the [5s, 5m] preset bounds.
 * @param {*} raw the raw stored or incoming value.
 * @returns {number} a sane interval in ms (0 = polling disabled).
 */
export const sanitizeRefreshIntervalMs = (raw) => {
    if (raw === null || raw === undefined || raw === '') return MANAGEMENT_REFRESH_DEFAULT_MS;
    const n = Number(raw);
    if (Number.isNaN(n)) return MANAGEMENT_REFRESH_DEFAULT_MS;
    if (n <= 0) return 0;
    const allowed = MANAGEMENT_REFRESH_PRESETS.map((p) => p.value);
    if (allowed.includes(n)) return n;
    if (n < 5000) return 5000;
    if (n > 300000) return 300000;
    return n;
};

/**
 * Resolve the management analytics date window. Accepts EITHER a preset day count
 * OR an explicit custom range, while always returning the same shape so every
 * existing fetcher keeps working unchanged:
 *   `{days, startDate, endDate, dateFrom, dateTo}`.
 *
 * Supported call styles (backward compatible with all current callers):
 *   - `getManagementDateRange(30)` — preset last-N-days (unchanged behaviour).
 *   - `getManagementDateRange({startDate, endDate})` — explicit custom range.
 *   - `getManagementDateRange(null, {startDate, endDate})` — explicit custom range
 *     (2-arg form, handy when a preset slot must stay `null`).
 *
 * For a preset call `days` is the numeric day count; for a custom range `days` is
 * `null` and `startDate`/`endDate` come straight from the supplied bounds (start
 * normalised to 00:00:00, end to 23:59:59). `startDate`/`endDate` may be `Date`
 * objects or ISO/parsable strings.
 *
 * @param {number|{startDate?: (Date|string), endDate?: (Date|string)}|null} [daysOrRange=30]
 *   a preset day count, or a custom `{startDate, endDate}` object.
 * @param {{startDate?: (Date|string), endDate?: (Date|string)}|null} [customRange=null]
 *   an explicit custom range when the first argument is a preset/`null`.
 * @returns {{days: (number|null), startDate: string, endDate: string, dateFrom: string, dateTo: string}}
 */
export const getManagementDateRange = (daysOrRange = 30, customRange = null) => {
    const custom = customRange
        || (daysOrRange && typeof daysOrRange === 'object' ? daysOrRange : null);

    if (custom && (custom.startDate || custom.endDate)) {
        const start = custom.startDate ? new Date(custom.startDate) : new Date();
        const end = custom.endDate ? new Date(custom.endDate) : new Date();
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return {
            days: null,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            dateFrom: toDateOnly(start),
            dateTo: toDateOnly(end),
        };
    }

    const days = Number(daysOrRange) || 30;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return {
        days,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        dateFrom: toDateOnly(start),
        dateTo: toDateOnly(end),
    };
};

export const fetchManagementCrmStatistics = async (scope) => {
    const data = await apiFetch('/crm/statistics', {headers: scopeHeaders(scope)});
    return data?.statistics || data?.data || data || null;
};

/* -------------------------------------------------------------------------- */
/*  Resilient dashboard aggregator                                              */
/*  Pulls every business-area dataset in parallel. A failure in one module      */
/*  never breaks the dashboard — that module simply reports as empty/null.      */
/* -------------------------------------------------------------------------- */
/**
 * Datasets that rarely change within a session (catalog / reference data). The
 * dashboard's background poll skips them (see `tier: 'live'` below) — on a store
 * with an 80k-product catalog and thousands of customers, re-pulling them every
 * minute is pure server + parse + re-render churn.
 */
export const MANAGEMENT_STATIC_DATASETS = ['foods', 'customers', 'technicians', 'banks', 'costTypes', 'warehouses'];

/**
 * @param {{restaurantId?: string, placeId?: string}} scope active management scope.
 * @param {ReturnType<typeof getManagementDateRange>} [range] analytics window.
 * @param {object} [options] additive, optional refinements (omit for the legacy "all 17 at once" call).
 * @param {'all'|'live'} [options.tier='all'] `live` skips {@link MANAGEMENT_STATIC_DATASETS}; they are simply absent from the result.
 * @param {(key: string, value: *) => void} [options.onPartial] called as EACH dataset settles, so the caller can paint
 *   incrementally instead of waiting for the slowest request.
 * @returns {Promise<object>} the keyed datasets that were fetched.
 */
export const fetchManagementDashboardData = async (scope, range = getManagementDateRange(30), options = {}) => {
    const {tier = 'all', onPartial = null} = options || {};
    const skipStatic = tier === 'live';
    const result = {};
    const settle = (key, factory, fallback) => {
        if (skipStatic && MANAGEMENT_STATIC_DATASETS.includes(key)) return Promise.resolve();

        return Promise.resolve()
            .then(factory)
            .then((value) => (value === undefined ? fallback : value))
            .catch(() => fallback)
            .then((value) => {
                const emit = (name, data) => {
                    result[name] = data;
                    if (typeof onPartial === 'function') {
                        try {
                            onPartial(name, data);
                        } catch (_error) { /* a faulty listener must never break the aggregate */ }
                    }
                };

                // `foods` arrives as the paginated `{items, pagination}` envelope: keep `foods` a flat array
                // (legacy shape) and publish the catalog size separately as `foodsTotal`.
                if (key === 'foods' && value && !Array.isArray(value) && Array.isArray(value.items)) {
                    emit('foods', value.items);
                    emit('foodsTotal', Number.isFinite(Number(value.pagination?.total)) ? Number(value.pagination.total) : null);

                    return;
                }
                emit(key, value);
            });
    };

    await Promise.all([
        settle('orders', () => fetchManagementOrders(scope, range), []),
        // Only the catalog SIZE is shown on the dashboard, so ask for a single row and read the
        // server's total instead of shipping (and parsing) up to 100 products every refresh.
        settle('foods', () => fetchManagementFoods(scope, {limit: 1}), []),
        settle('stock', () => fetchManagementStock(scope), []),
        settle('leads', () => fetchManagementLeads(scope), []),
        settle('crmStats', () => fetchManagementCrmStatistics(scope), null),
        settle('customers', () => fetchManagementCustomers(scope), []),
        settle('quotes', () => fetchManagementQuotes(scope), []),
        settle('workOrders', () => fetchManagementWorkOrders(scope), []),
        settle('technicians', () => fetchManagementTechnicians(scope), []),
        settle('appointments', () => fetchManagementAppointments(scope), []),
        settle('invoicesInbound', () => fetchManagementInvoices(scope, {direction: 'INBOUND', range, unboundedFallback: false}), []),
        settle('invoicesOutbound', () => fetchManagementInvoices(scope, {direction: 'OUTBOUND', range, unboundedFallback: false}), []),
        settle('banks', () => fetchManagementBanks(scope), []),
        settle('registers', () => fetchManagementRegisters(scope), []),
        settle('costTypes', () => fetchManagementCostTypes(scope), []),
        settle('warehouses', () => fetchManagementWarehouses(scope), []),
        settle('transactions', () => fetchManagementTransactions(scope), []),
    ]);

    return result;
};

/* -------------------------------------------------------------------------- */
/*  Analytics builders for the charts                                           */
/* -------------------------------------------------------------------------- */

/** Best-effort timestamp for an order (explicit date field, else ObjectId). */
export const getOrderDate = (order) => {
    const raw = order?.created || order?.createdAt || order?.orderDate || order?.date
        || order?.closedAt || order?.closedDate || order?.paymentDate;
    if (raw) {
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return d;
    }
    const id = String(order?._id || order?.id || '');
    if (/^[a-f\d]{24}$/i.test(id)) {
        const seconds = parseInt(id.substring(0, 8), 16);
        if (seconds) return new Date(seconds * 1000);
    }
    return null;
};

const numericSum = (order) => Number(order?.orderSum || order?.productsSum || 0);

/**
 * Build a bucketed daily/weekly revenue series for a bar chart.
 * Targets ~`buckets` columns so the chart stays readable for any range.
 */
export const buildRevenueSeries = (orders = [], days = 30, buckets = 8) => {
    const accepted = (Array.isArray(orders) ? orders : []).filter(
        (o) => !o?.storno && String(o?.status || '').toLowerCase() !== 'cancelled',
    );
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const totalDays = Math.max(1, Number(days) || 30);
    const bucketCount = Math.min(buckets, totalDays);
    const bucketDays = Math.ceil(totalDays / bucketCount);

    const series = Array.from({length: bucketCount}, (_, i) => {
        const bucketEnd = new Date(end);
        bucketEnd.setDate(end.getDate() - (bucketCount - 1 - i) * bucketDays);
        const bucketStart = new Date(bucketEnd);
        bucketStart.setDate(bucketEnd.getDate() - (bucketDays - 1));
        bucketStart.setHours(0, 0, 0, 0);
        return {start: bucketStart, end: bucketEnd, value: 0};
    });

    accepted.forEach((order) => {
        const date = getOrderDate(order);
        if (!date) return;
        for (const bucket of series) {
            if (date >= bucket.start && date <= bucket.end) {
                bucket.value += numericSum(order);
                break;
            }
        }
    });

    const fmtShort = (d) => `${d.getMonth() + 1}.${d.getDate()}`;
    return series.map((bucket) => ({
        label: bucketDays > 1 ? fmtShort(bucket.end) : fmtShort(bucket.start),
        fullLabel: bucketDays > 1
            ? `${fmtShort(bucket.start)}–${fmtShort(bucket.end)}`
            : bucket.start.toLocaleDateString('hu-HU', {month: 'short', day: 'numeric'}),
        value: Math.round(bucket.value),
    }));
};

/** Sales channel split (dine-in / take-away / online) from computed stats. */
export const buildChannelDistribution = (summary = {}) => {
    const sums = summary?.allSum || {};
    return [
        {label: i18n.t('management_channel_dine_in'), value: Math.round(sums.dineIn || 0), color: '#F97316'},
        {label: i18n.t('management_channel_takeaway'), value: Math.round(sums.takeAway || 0), color: '#3B82F6'},
        {label: i18n.t('management_channel_online'), value: Math.round(sums.online || 0), color: '#8B5CF6'},
    ];
};

/** Payment-method split (cash / card / VIP) from computed stats. */
export const buildPaymentDistribution = (summary = {}) => {
    const sums = summary?.allSum || {};
    return [
        {label: i18n.t('management_payment_cash'), value: Math.round(summary.fullCashIncome || 0), color: '#10B981'},
        {label: i18n.t('management_payment_card'), value: Math.round(summary.creditCardOrdersSum || 0), color: '#3B82F6'},
        {label: i18n.t('management_payment_vip_balance'), value: Math.round(sums.vipBalance || 0), color: '#8B5CF6'},
    ];
};

/** Period momentum: compare 2nd half vs 1st half of the range (no extra fetch). */
export const buildMomentum = (orders = []) => {
    const dated = (Array.isArray(orders) ? orders : [])
        .filter((o) => !o?.storno && String(o?.status || '').toLowerCase() !== 'cancelled')
        .map((o) => ({date: getOrderDate(o), sum: numericSum(o)}))
        .filter((o) => o.date)
        .sort((a, b) => a.date - b.date);

    if (dated.length < 2) return 0;
    const mid = dated[Math.floor(dated.length / 2)].date.getTime();
    let first = 0;
    let second = 0;
    dated.forEach((o) => {
        if (o.date.getTime() < mid) first += o.sum;
        else second += o.sum;
    });
    if (first <= 0) return second > 0 ? 100 : 0;
    return Math.round(((second - first) / first) * 100);
};

export const buildManagementStatistics = (orders = []) => {
    const safeOrders = (Array.isArray(orders) ? orders : []).map((order) => ({
        ...order,
        orderFrom: order?.orderFrom || '',
        orderSum: Number(order?.orderSum || 0),
        productsSum: Number(order?.productsSum || 0),
        deliveryFee: Number(order?.deliveryFee || 0),
        serviceFeePrice: Number(order?.serviceFeePrice || 0),
        tipAmount: Number(order?.tipAmount || 0),
        history: Array.isArray(order?.history) ? order.history : [],
    }));
    const result = StatisticsHelper(safeOrders);
    const stats = result?.statistics || {};
    const paymentStatistics = result?.paymentStatistics || {};
    const gross = Number(stats?.finalSum || stats?.ordersSum || 0);
    // Count every fetched order, not just the paid ones surfaced in
    // paymentStatistics.all — otherwise a pending/unpaid order exists in the
    // order list but the dashboard reports 0 (see QA MGMT-012).
    const safeOrderCount = Array.isArray(orders) ? orders.length : 0;
    const paidCount = Array.isArray(paymentStatistics?.all) ? paymentStatistics.all.length : 0;
    const orderCount = Math.max(safeOrderCount, paidCount);
    const averageBasket = paidCount > 0 ? gross / paidCount : 0;

    return {
        ...stats,
        productsSum: result?.productsSum || 0,
        paymentStatistics,
        gross,
        orderCount,
        averageBasket,
    };
};










/* -------------------------------------------------------------------------- */
/*  Automations (system-wide automation engine — /api/automations)            */
/* -------------------------------------------------------------------------- */

/**
 * List automations visible to the current user (restaurant-wide + own private).
 * @param {object} scope
 * @returns {Promise<object[]>}
 */
export const fetchManagementAutomations = async (scope) => {
    if (!scope?.restaurantId) return [];
    return normalizeList(await apiFetch('/automations?limit=200'), ['docs', 'items']);
};

/**
 * Fetch the list of automation-capable modules (Lead, Order, Food, Task…).
 * @returns {Promise<object[]>}
 */
export const fetchAutomationModules = async () => normalizeList(await apiFetch('/automations/modules'), ['items', 'docs']);

/**
 * Turn a natural-language prompt into a draft automation (not persisted).
 * @param {object} scope
 * @param {string} prompt
 * @returns {Promise<object>} `{ kind, payload }`
 */
export const generateAutomationDraft = async (scope, prompt) => {
    if (!scope?.restaurantId) throw new Error(i18n.t('management_error_missing_restaurant_id'));
    return apiFetch('/automations/ai/generate', {method: 'POST', body: JSON.stringify({prompt})});
};

/**
 * Create a new automation scoped to the current restaurant.
 * @param {object} scope
 * @param {object} payload
 * @returns {Promise<object>}
 */
export const createManagementAutomation = async (scope, payload) => {
    if (!scope?.restaurantId) throw new Error(i18n.t('management_error_missing_restaurant_id'));
    const body = {...payload, scope: {...(payload.scope || {}), restaurantId: scope.restaurantId}};
    return apiFetch('/automations', {method: 'POST', body: JSON.stringify(body)});
};

/**
 * Update an existing automation.
 * @param {string} id
 * @param {object} payload
 * @returns {Promise<object>}
 */
export const updateManagementAutomation = async (id, payload) => apiFetch(`/automations/${id}`, {method: 'PUT', body: JSON.stringify(payload)});

/**
 * Enable/disable an automation.
 * @param {string} id
 * @param {boolean} enabled
 * @returns {Promise<object>}
 */
export const toggleManagementAutomation = async (id, enabled) => apiFetch(`/automations/${id}/${enabled ? 'enable' : 'disable'}`, {method: 'POST'});

/**
 * Permanently delete an automation.
 * @param {string} id
 * @returns {Promise<object>}
 */
export const deleteManagementAutomation = async (id) => apiFetch(`/automations/${id}`, {method: 'DELETE'});

/* -------------------------------------------------------------------------- */
/*  Custom roles (dynamic role templates) + permission catalog                */
/* -------------------------------------------------------------------------- */

/**
 * Fetch the permission/visibility catalog (menu / page / function keys) — the
 * source of truth the custom-role editor tree is built from. Backend-driven so
 * newer modules (rental, meetings, voip, …) appear automatically.
 * @returns {Promise<Array<{kind: string, module: string, key: string, defaultLabel?: string}>>}
 */
export const fetchPermissionCatalog = async () => {
    const res = await apiFetch('/permission-definitions');

    return Array.isArray(res?.definitions) ? res.definitions : (Array.isArray(res) ? res : []);
};

/**
 * List the active store's custom role templates (dynamic roles).
 * @returns {Promise<object[]>}
 */
export const listRoleTemplates = async () => {
    const scope = await getManagementScope();
    const res = await apiFetch('/role-templates', {headers: scopeHeaders(scope)});

    return Array.isArray(res?.dynamicRoles)
        ? res.dynamicRoles
        : (Array.isArray(res?.roles) ? res.roles : (Array.isArray(res) ? res : []));
};

/**
 * Create a custom role template scoped to the active restaurant.
 * @param {object} payload `{ name, key?, description?, status?, inheritsRoles[], permissions[], visibility{} }`
 * @returns {Promise<object>}
 */
export const createRoleTemplate = async (payload) => {
    const scope = await getManagementScope();

    return apiFetch('/role-templates', {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify({...payload, restaurantId: scope?.restaurantId}),
    });
};

/**
 * Update an existing custom role template.
 * @param {string} id
 * @param {object} payload
 * @returns {Promise<object>}
 */
export const updateRoleTemplate = async (id, payload) => {
    const scope = await getManagementScope();

    return apiFetch(`/role-templates/${id}`, {
        method: 'PUT',
        headers: scopeHeaders(scope),
        body: JSON.stringify({...payload, restaurantId: scope?.restaurantId}),
    });
};

/**
 * Delete a custom role template.
 * @param {string} id
 * @returns {Promise<object>}
 */
export const deleteRoleTemplate = async (id) => {
    const scope = await getManagementScope();

    return apiFetch(`/role-templates/${id}`, {method: 'DELETE', headers: scopeHeaders(scope)});
};

/**
 * Turn a natural-language role description into a validated permission set (AI).
 * @param {string} prompt
 * @param {string} [locale]
 * @returns {Promise<{permissions: string[], visibility: object, inheritsRoles: string[], explanation: string}>}
 */
export const generateRoleConfigAi = async (prompt, locale) => {
    const scope = await getManagementScope();
    const body = {prompt};

    if (locale) body.locale = locale;

    return apiFetch('/ai/tools/role-config', {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify(body),
    });
};

/* -------------------------------------------------------------------------- */
/*  Module templates (store-level `enabledModuleIds` bundles)                 */
/* -------------------------------------------------------------------------- */

/**
 * List module templates visible to the caller — built-in presets merged with
 * the account's own custom templates.
 * @param {object} [options]
 * @param {boolean} [options.includeDisabled] Include soft-disabled custom templates (management view).
 * @param {string} [options.placeType] Filter to templates targeting this business type.
 * @param {string} [options.search]
 * @returns {Promise<object[]>}
 */
export const fetchModuleTemplates = async ({includeDisabled, placeType, search} = {}) => {
    const scope = await getManagementScope();
    const query = new globalThis.URLSearchParams();

    if (includeDisabled) query.set('includeDisabled', 'true');
    if (placeType) query.set('placeType', placeType);
    if (search) query.set('search', search);

    const qs = query.toString();
    const res = await apiFetch(`/module-templates${qs ? `?${qs}` : ''}`, {headers: scopeHeaders(scope)});

    return Array.isArray(res?.data?.templates) ? res.data.templates : (Array.isArray(res?.templates) ? res.templates : []);
};

/**
 * The localized sidebar-module catalog (sections + governable/core flags) —
 * source of truth for the module checklist in the template editor.
 * @returns {Promise<{sections: object[], modules: object[]}>}
 */
export const fetchModuleCatalog = async () => {
    const scope = await getManagementScope();
    const res = await apiFetch('/module-templates/catalog', {headers: scopeHeaders(scope)});

    return res?.data?.catalog || res?.catalog || {sections: [], modules: []};
};

/**
 * Create a custom account module template.
 * @param {object} payload `{ name, description?, icon?, color?, placeTypes[], moduleIds[] }`
 * @returns {Promise<object>}
 */
export const createModuleTemplate = async (payload) => {
    const scope = await getManagementScope();
    const res = await apiFetch('/module-templates', {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify({...payload, restaurantId: scope?.restaurantId}),
    });

    return res?.data?.template || res?.template || res;
};

/**
 * Update a custom module template (name/description/icon/color/placeTypes/
 * moduleIds/disabled). Built-in presets cannot be updated.
 * @param {string} id
 * @param {object} payload
 * @returns {Promise<object>}
 */
export const updateModuleTemplate = async (id, payload) => {
    const scope = await getManagementScope();
    const res = await apiFetch(`/module-templates/${id}`, {
        method: 'PUT',
        headers: scopeHeaders(scope),
        body: JSON.stringify(payload),
    });

    return res?.data?.template || res?.template || res;
};

/**
 * Delete a custom module template. Built-in presets cannot be deleted.
 * @param {string} id
 * @returns {Promise<object>}
 */
export const deleteModuleTemplate = async (id) => {
    const scope = await getManagementScope();

    return apiFetch(`/module-templates/${id}`, {method: 'DELETE', headers: scopeHeaders(scope)});
};

/**
 * AI-generate a module template draft from a free-text business description.
 * Metered against the store's AI-credit wallet. Returns a draft (not
 * persisted) the caller can review, edit, and save via `createModuleTemplate`.
 * @param {object} payload `{ businessType, description?, placeTypes? }`
 * @returns {Promise<{draft: object, usage: object}>}
 */
export const generateModuleTemplateAi = async (payload) => {
    const scope = await getManagementScope();
    const res = await apiFetch('/module-templates/generate', {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify({...payload, restaurantId: scope?.restaurantId}),
    });

    return res?.data || res;
};

/* -------------------------------------------------------------------------- */
/*  Service modes (dynamic service-module feature/field/catalog templates)    */
/* -------------------------------------------------------------------------- */

const SERVICE_MODE_BASE = '/service-modes';
const SERVICE_BRAND_BASE = '/service-brands';

/**
 * List service modes visible to the caller — code presets (`preset:*`)
 * merged with the restaurant's own + system rows.
 * @param {object} [options]
 * @param {string} [options.category] Filter by category key (e.g. `vehicle`, `electronics`).
 * @param {string} [options.search] Free-text filter over name/description.
 * @returns {Promise<object[]>}
 */
export const fetchServiceModes = async ({category, search} = {}) => {
    const scope = await getManagementScope();
    const query = new globalThis.URLSearchParams();

    if (category && category !== 'all') query.set('category', category);
    if (search) query.set('search', search);

    const qs = query.toString();
    const res = await apiFetch(`${SERVICE_MODE_BASE}${qs ? `?${qs}` : ''}`, {headers: scopeHeaders(scope)});

    return Array.isArray(res?.data?.modes) ? res.data.modes : (Array.isArray(res?.modes) ? res.modes : (Array.isArray(res) ? res : []));
};

/**
 * The localized service-mode catalog — 27 feature keys, field registry
 * sections, and the categorized preset groups.
 * @returns {Promise<{features: object[], fields: object, categories: object[]}>}
 */
export const fetchServiceModeCatalog = async () => {
    const scope = await getManagementScope();
    const res = await apiFetch(`${SERVICE_MODE_BASE}/catalog`, {headers: scopeHeaders(scope)});

    return res?.data || res || {};
};

/**
 * Fetch a single service mode (`modeId` may be a code preset id, e.g.
 * `preset:autoszerviz`).
 * @param {string} modeId
 * @returns {Promise<object>}
 */
export const fetchServiceMode = async (modeId) => {
    const scope = await getManagementScope();
    const res = await apiFetch(`${SERVICE_MODE_BASE}/${modeId}`, {headers: scopeHeaders(scope)});

    return res?.data || res;
};

/**
 * Create a new (own) service mode.
 * @param {object} payload `{name, description?, icon?, category?, featureToggles?, fieldSettings?, ...catalogs}`
 * @returns {Promise<object>}
 */
export const createServiceMode = async (payload) => {
    const scope = await getManagementScope();
    const res = await apiFetch(SERVICE_MODE_BASE, {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify({...payload, restaurantId: scope?.restaurantId}),
    });

    return res?.data || res;
};

/**
 * Update an own/system service mode. Code presets are immutable (403) —
 * duplicate them first.
 * @param {string} modeId
 * @param {object} payload
 * @returns {Promise<object>}
 */
export const updateServiceMode = async (modeId, payload) => {
    const scope = await getManagementScope();
    const res = await apiFetch(`${SERVICE_MODE_BASE}/${modeId}`, {
        method: 'PUT',
        headers: scopeHeaders(scope),
        body: JSON.stringify(payload),
    });

    return res?.data || res;
};

/**
 * Delete an own service mode. Presets/system rows cannot be deleted.
 * @param {string} modeId
 * @returns {Promise<object>}
 */
export const deleteServiceMode = async (modeId) => {
    const scope = await getManagementScope();

    return apiFetch(`${SERVICE_MODE_BASE}/${modeId}`, {method: 'DELETE', headers: scopeHeaders(scope)});
};

/**
 * Fork a mode (preset or another own mode) into an editable restaurant copy.
 * @param {string} modeId
 * @returns {Promise<object>}
 */
export const duplicateServiceMode = async (modeId) => {
    const scope = await getManagementScope();
    const res = await apiFetch(`${SERVICE_MODE_BASE}/${modeId}/duplicate`, {
        method: 'POST',
        headers: scopeHeaders(scope),
    });

    return res?.data || res;
};

/**
 * Apply a mode to the restaurant's live `ServiceConfig` — the mobile/admin
 * service module immediately reflects the new feature toggles/fields/catalogs.
 * @param {string} modeId
 * @returns {Promise<object>}
 */
export const applyServiceMode = async (modeId) => {
    const scope = await getManagementScope();
    const res = await apiFetch(`${SERVICE_MODE_BASE}/${modeId}/apply`, {
        method: 'POST',
        headers: scopeHeaders(scope),
    });

    return res?.data || res;
};

/**
 * AI-generate a service-mode draft from a free-text prompt. Metered against
 * the store's AI-credit wallet. Returns a draft (not persisted) the caller
 * can review, edit, and save via `createServiceMode`.
 * @param {object} payload `{prompt, locale?, name?}`
 * @returns {Promise<{draft: object, usage: object}>}
 */
export const generateServiceModeAi = async (payload) => {
    const scope = await getManagementScope();
    const res = await apiFetch(`${SERVICE_MODE_BASE}/generate`, {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify({...payload, restaurantId: scope?.restaurantId}),
    });

    return res?.data || res;
};

/**
 * List the restaurant's own brand catalog rows (CRUD source of truth).
 * @param {object} [options]
 * @param {string} [options.search]
 * @returns {Promise<object[]>}
 */
export const fetchServiceBrands = async ({search} = {}) => {
    const scope = await getManagementScope();
    const query = new globalThis.URLSearchParams();

    if (search) query.set('search', search);
    const qs = query.toString();
    const res = await apiFetch(`${SERVICE_BRAND_BASE}${qs ? `?${qs}` : ''}`, {headers: scopeHeaders(scope)});

    return Array.isArray(res?.data?.brands) ? res.data.brands : (Array.isArray(res?.brands) ? res.brands : (Array.isArray(res) ? res : []));
};

/**
 * The effective (merged) brand catalog used by vehicle pickers — falls back
 * to the bundled `VEHICLE_BRANDS` on the backend when the restaurant has
 * zero rows and `vehicles` is enabled.
 * @returns {Promise<object[]>}
 */
export const fetchMergedBrands = async () => {
    const scope = await getManagementScope();
    const res = await apiFetch(`${SERVICE_BRAND_BASE}/effective`, {headers: scopeHeaders(scope)});

    return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
};

/**
 * Create a brand row (`{name, models?}`) — the restaurant's own catalog.
 * The controller answers `result(res, 201, {brand})`, so the row itself sits
 * one level below `data` — exactly like `fetchServiceBrands` reads
 * `data.brands`. Callers get the brand row, never the envelope.
 * @param {object} payload
 * @returns {Promise<object>} The created brand row (`{_id, name, models, modelCatalog, types, ...}`).
 */
export const createServiceBrand = async (payload) => {
    const scope = await getManagementScope();
    const res = await apiFetch(SERVICE_BRAND_BASE, {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify({...payload, restaurantId: scope?.restaurantId}),
    });

    return res?.data?.brand || res?.data || res;
};

/**
 * Update a brand row. Editing a `preset`/`ai` row forks it into a `user` row
 * server-side (copy-on-write). The controller answers
 * `result(res, 200, {brand})`, so the row is unwrapped out of that envelope
 * — callers get the brand row itself, never `{brand}`.
 * @param {string} brandId
 * @param {object} payload
 * @returns {Promise<object>} The updated brand row (`{_id, name, models, modelCatalog, types, ...}`).
 */
export const updateServiceBrand = async (brandId, payload) => {
    const scope = await getManagementScope();
    const res = await apiFetch(`${SERVICE_BRAND_BASE}/${brandId}`, {
        method: 'PUT',
        headers: scopeHeaders(scope),
        body: JSON.stringify(payload),
    });

    return res?.data?.brand || res?.data || res;
};

/**
 * Soft-delete a brand row.
 * @param {string} brandId
 * @returns {Promise<object>}
 */
export const deleteServiceBrand = async (brandId) => {
    const scope = await getManagementScope();

    return apiFetch(`${SERVICE_BRAND_BASE}/${brandId}`, {method: 'DELETE', headers: scopeHeaders(scope)});
};

/**
 * Append a model and/or a type (variant) option to an existing brand row —
 * the persistence behind the "+ Add …" rows of the vehicle pickers, so a
 * value typed once on the phone is offered on the next work order. The
 * backend append is idempotent and case-insensitive (never a second "Avant"
 * next to "avant"), and appending to a `preset`/`ai` row forks it into a
 * `user` row (copy-on-write, exactly like `updateServiceBrand`).
 *
 * A `type` sent together with a `model` lands on that model's type list; a
 * `type` sent alone lands on the brand-level list (applies to every model).
 * @param {string} brandId The `ServiceBrand` row id.
 * @param {object} [payload] `{model?: string, type?: string}` — at least one must be non-blank (400 otherwise).
 * @param {object} [scope] Optional pre-resolved management scope; defaults to {@link getManagementScope}.
 * @returns {Promise<object>} The updated brand row (`{_id, name, models, modelCatalog, types, ...}`) — unwrapped out of the `{brand}` envelope the controller answers with.
 */
export const addServiceBrandOption = async (brandId, {model, type} = {}, scope = null) => {
    const resolvedScope = scope || await getManagementScope();
    const res = await apiFetch(`${SERVICE_BRAND_BASE}/${brandId}/options`, {
        method: 'POST',
        headers: scopeHeaders(resolvedScope),
        body: JSON.stringify({model, type}),
    });

    return res?.data?.brand || res?.data || res;
};

/* -------------------------------------------------------------------------- */
/*  User account/session administration (status, unlock, dynamic-role assign) */
/* -------------------------------------------------------------------------- */

/**
 * Flip a user's active/inactive `status` flag — the same field and endpoint
 * admin's employee cards use (`PUT /admin/user/:id`, body `{status}`).
 * @param {string} userId
 * @param {boolean} nextStatus
 * @returns {Promise<object>}
 */
export const setManagementUserStatus = async (userId, nextStatus) => apiFetch(`/admin/user/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({status: !!nextStatus}),
});

/**
 * Reset a user's failed-login lockout (`authLockout`), mirroring admin's
 * "Reset lock" action.
 * @param {string} userId
 * @returns {Promise<object>}
 */
export const unlockManagementUser = async (userId) => apiFetch(`/admin/user/${userId}/unlock`, {method: 'POST'});

/**
 * Replace the set of dynamic role templates (and optionally the static
 * legacy roles) assigned to a user for the active store — the mobile
 * counterpart of admin's `CustomRoleAssignmentModal` / `assignDynamicRoles`.
 * Only templates with `status: active` in the target store are applied;
 * unknown/inactive ids are silently dropped server-side.
 * @param {string} userId
 * @param {string[]} dynamicRoleIds
 * @param {string[]} [roles] Optional static legacy roles to replace as well.
 * @returns {Promise<object>} The refreshed user.
 */
export const assignRoleTemplatesToUser = async (userId, dynamicRoleIds, roles) => {
    const scope = await getManagementScope();

    return apiFetch(`/role-templates/users/${userId}`, {
        method: 'PUT',
        headers: scopeHeaders(scope),
        body: JSON.stringify({
            restaurantId: scope?.restaurantId,
            dynamicRoleIds: Array.isArray(dynamicRoleIds) ? dynamicRoleIds : [],
            ...(Array.isArray(roles) ? {roles} : {}),
        }),
    });
};

/* -------------------------------------------------------------------------- */
/*  Quote lifecycle (mobile quote builder) — line items, send, timeline,        */
/*  revisions and contract generation. GET/PUT/DELETE mirror the verified       */
/*  `/api/quotes/*` contract; every write carries the scope header the same     */
/*  way {@link createManagementQuote} already does, so a multi-store manager's  */
/*  edit lands on the store they actually have selected.                        */
/* -------------------------------------------------------------------------- */

/**
 * Fetch a single quote with its lead/customer/store populated
 * (GET /quotes/{id}).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} quoteId the quote's `_id`.
 * @returns {Promise<object>} the quote.
 */
export const getQuoteById = async (scope, quoteId) => apiFetch(
    `/quotes/${encodeURIComponent(quoteId)}`,
    {headers: scopeHeaders(scope)},
);

/**
 * Partially update a quote (PUT /quotes/{id}). Omitting `items`/`discounts`
 * leaves them untouched server-side; sending `items` REPLACES the whole array
 * (recomputes totalNet/totalVat/totalGross), and sending `discounts` REPLACES
 * that array too — this editor never sends `discounts`, so the store's
 * structured discounts (if any) are always left exactly as they were.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} quoteId the quote's `_id`.
 * @param {object} payload fields to update.
 * @returns {Promise<object>} the updated quote.
 */
export const updateQuote = async (scope, quoteId, payload) => apiFetch(`/quotes/${encodeURIComponent(quoteId)}`, {
    method: 'PUT',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Soft-delete a quote (DELETE /quotes/{id}).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @returns {Promise<object>} the backend response.
 */
export const deleteQuote = async (scope, item) => apiFetch(`/quotes/${encodeURIComponent(idOf(item))}`, {
    method: 'DELETE',
    headers: scopeHeaders(scope),
});

/**
 * Transition a quote to `accepted` or `rejected` (PATCH /quotes/{id}/status —
 * the ONLY two values that verb accepts; `draft`/`sent`/`cancelled` must go
 * through {@link updateQuote}'s generic PUT instead). A quote already in a
 * finalized state (`closed`) answers 409.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @param {'accepted'|'rejected'} status the target status.
 * @returns {Promise<object>} the updated quote.
 */
export const updateQuoteStatus = async (scope, item, status) => {
    if (status !== 'accepted' && status !== 'rejected') {
        throw new Error(i18n.t('sales.quote.invalidStatusTransition', {defaultValue: 'Az árajánlat állapota csak elfogadott vagy elutasított lehet.'}));
    }

    // A quote row/detail resolved before its backend record loaded (or one
    // stripped of its identifier by an upstream merge) previously reached
    // here as `undefined`, so encodeURIComponent(undefined) produced a
    // literal 'PATCH /quotes/undefined/status' — the backend then threw a
    // Mongoose CastError it answered as a bare 500 (HAPPY-API-8B). Fail fast
    // client-side instead, matching updateManagementStatus's own guard.
    const id = idOf(item);

    if (!id) throw new Error(i18n.t('management_error_missing_id_status'));

    return apiFetch(`/quotes/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        headers: scopeHeaders(scope),
        body: JSON.stringify({status}),
    });
};

/**
 * E-mail the quote to its recipient — server-side render + send
 * (POST /quotes/{id}/send). See the swagger `QuoteSendInput`/`QuoteSendResult`
 * definitions: 400 `NO_RECIPIENT`, 502 `MAIL_TRANSPORT_FAILED`.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @param {{to?: string, cc?: string[], subject?: string, message?: string,
 *   templateId?: string, includePdf?: boolean, includeAttachments?: boolean,
 *   markAsSent?: boolean, lang?: string}} [payload]
 * @returns {Promise<{sentTo: string, cc: string[], publicUrl: string,
 *   pdf: object|null, skippedAttachments: string[], warnings: string[],
 *   status: string, version: number}>}
 */
export const sendQuoteEmail = async (scope, item, payload = {}) => apiFetch(`/quotes/${encodeURIComponent(idOf(item))}/send`, {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Mint (idempotently) the quote's public accept/reject landing link
 * (POST /quotes/{id}/public-link). Promotes a `draft` quote to `sent`.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @param {string} [lang] locale for the landing page.
 * @returns {Promise<{token: string, url: string, status: string, version: number}>}
 */
export const generateQuotePublicLink = async (scope, item, lang) => apiFetch(
    `/quotes/${encodeURIComponent(idOf(item))}/public-link${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`,
    {method: 'POST', headers: scopeHeaders(scope)},
);

/**
 * Send an SMS via the generic CRM messaging endpoint (POST /crm/sms) — used
 * by the quote "Send by SMS" flow after minting the public link with
 * {@link generateQuotePublicLink}. 402 means the store's SMS-credit balance
 * ran out for the current billing period.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {{to?: string, message: string, leadId?: string, customerId?: string}} payload
 * @returns {Promise<{success: boolean, to: string, segments: number, remainingCredits: number}>}
 */
export const sendCrmSms = async (scope, payload) => apiFetch('/crm/sms', {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Read a quote's own activity trail (GET /quotes/{id}/timeline).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @returns {Promise<object[]>} timeline entries, most recent first.
 */
export const getQuoteTimeline = async (scope, item) => normalizeList(
    await apiFetch(`/quotes/${encodeURIComponent(idOf(item))}/timeline`, {headers: scopeHeaders(scope)}),
    ['timeline', 'events'],
);

/**
 * Append a free-text note to a quote's timeline (POST /quotes/{id}/timeline).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @param {string} message the note text.
 * @returns {Promise<object>} the backend response.
 */
export const addQuoteTimelineNote = async (scope, item, message) => apiFetch(`/quotes/${encodeURIComponent(idOf(item))}/timeline`, {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify({message}),
});

/**
 * List a quote's past versions (GET /quotes/{id}/revisions) — populated
 * whenever an edit to a non-draft quote minted a new version.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @returns {Promise<object[]>} revision summaries.
 */
export const getQuoteRevisions = async (scope, item) => normalizeList(
    await apiFetch(`/quotes/${encodeURIComponent(idOf(item))}/revisions`, {headers: scopeHeaders(scope)}),
    ['revisions'],
);

/**
 * Restore a quote to a past version (POST /quotes/{id}/revisions/{version}/restore).
 * The restore itself becomes a new version — mints another revision rather
 * than rewriting history.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @param {number|string} version the version number to restore.
 * @param {string} [note] optional reason, stored on the timeline.
 * @returns {Promise<object>} the restored quote.
 */
export const restoreQuoteRevision = async (scope, item, version, note) => apiFetch(
    `/quotes/${encodeURIComponent(idOf(item))}/revisions/${encodeURIComponent(version)}/restore`,
    {method: 'POST', headers: scopeHeaders(scope), body: JSON.stringify(note ? {note} : {})},
);

/**
 * Should the "generate a contract?" prompt be raised for this (accepted)
 * quote, and with what template (GET /quotes/{id}/contract)? Read-only.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @returns {Promise<{shouldPrompt: boolean, templateId?: string, contract?: object}>}
 */
export const getQuoteContract = async (scope, item) => apiFetch(
    `/quotes/${encodeURIComponent(idOf(item))}/contract`,
    {headers: scopeHeaders(scope)},
);

/**
 * Generate the quote's contract (POST /quotes/{id}/contract). A 422 response
 * means the template still needs manual input — the body carries
 * `{needsInput: true, missing: [{key, label, type, group}]}`; retry with those
 * keys filled into `payload.manualValues`.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {object} item the quote record.
 * @param {{templateId?: string, manualValues?: object, attachToLead?: boolean,
 *   attachToCustomer?: boolean, convertLead?: boolean}} [payload]
 * @returns {Promise<object>} `{document, attachments, conversion, contract, quote}`.
 */
export const createQuoteContract = async (scope, item, payload = {}) => apiFetch(`/quotes/${encodeURIComponent(idOf(item))}/contract`, {
    method: 'POST',
    headers: scopeHeaders(scope),
    body: JSON.stringify(payload),
});

/**
 * Convert a Blob (RN fetch polyfill) into its base64 payload via `FileReader`
 * — the same binary→base64 bridge `expo-file-system`'s own `writeAsStringAsync`
 * consumes, and the format {@link QuoteDocumentScreen}'s share flow already
 * accepts through its `doc?.pdf?.base64` fallback branch.
 * @param {Blob} blob the fetched binary payload.
 * @returns {Promise<string>} the base64 (no `data:` prefix) payload.
 */
const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    // FileReader is a real RN/JSC runtime global; the shared eslint-config-expo
    // ruleset has no `browser` env and does not know about it (same pre-existing
    // gap as `Blob` in apiFetchBlob above).
    // eslint-disable-next-line no-undef
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Failed to read PDF preview blob'));
    reader.onload = () => {
        const result = String(reader.result || '');
        const comma = result.indexOf(',');

        resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
});

/**
 * Preview a quote as the FINAL PDF bytes (GET /quotes/{quoteId}/preview?format=pdf)
 * — a Puppeteer render with the quote's attached documents appended, streamed
 * inline WITHOUT persisting a GeneratedDocument (unlike {@link renderQuotePdf}).
 * Returns the base64 payload so the caller can write it to a cache file and
 * share/print it via `expo-file-system` + `expo-sharing`, exactly like
 * {@link QuoteDocumentScreen}'s existing `doc?.pdf?.base64` fallback branch.
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} quoteId the quote's `_id`.
 * @param {{templateId?: string}} [opts] preview a specific template instead of the selected one.
 * @returns {Promise<string>} base64-encoded PDF bytes.
 */
export const previewQuotePdf = async (scope, quoteId, opts = {}) => {
    const query = buildQuery({format: 'pdf', templateId: opts.templateId});
    const blob = await apiFetchBlob(
        `/quotes/${encodeURIComponent(quoteId)}/preview${query ? `?${query}` : ''}`,
        {headers: scopeHeaders(scope)},
    );

    return blobToBase64(blob);
};

/**
 * Search past quote line items for autocomplete
 * (GET /quotes/items/suggestions).
 * @param {{restaurantId?: string}} scope active management scope.
 * @param {string} [q] search term matched against past item names.
 * @returns {Promise<string[]>} up to 20 distinct past line-item names.
 */
export const getQuoteItemSuggestions = async (scope, q) => {
    const query = buildQuery({q});
    const body = await apiFetch(`/quotes/items/suggestions${query ? `?${query}` : ''}`, {headers: scopeHeaders(scope)});

    return Array.isArray(body) ? body : [];
};
